import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, useProgress } from '@react-three/drei';
import { cameraDragStore } from '../../store/cameraDragStore';
import * as THREE from 'three';
import SceneEnvironment3D from './SceneEnvironment3D';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  toggleView3d,
  closeView3d,
  toggleView3dDragLayout,
  setActive3DCameraPreset,
  setActive3DCameraOht,
  setActive3DFollowOht,
  setActive3DFollowOhtMode,
  toggleSupportPillars,
  setZoomLevel,
  setView3dSceneRevealed,
} from '../../features/ui/uiSlice';
import { stopSimulation } from '../../features/simulation/simulationSlice';
import { clearLayout3d } from '../../features/layout3d/layout3dSlice';
import { use3dLayoutBootstrap } from '../../hooks/use3dLayoutBootstrap';
import { useSimulationFrames, FrameVehicle } from '../../hooks/useSimulationFrames';
import { useViewport } from '../../hooks/useViewport';
import { isFloor3DElement } from '../../utils/layout3dFloor';
import { SMALL_DETAILED_MESH_LIMIT } from '../../utils/layout3dConstants';
import {
  isJunctionRailType,
  isCurveRailType,
  resolveRailType,
  getCurveRadius,
  getCurveSweepAngle,
} from '../../utils/topology';
import {
  EQUIPMENT_KIT_HEIGHT,
  getElement3DGroupY,
  getElement3DExtrusionDepth,
  resolveRailElevation,
} from '../../utils/layout3d';
import { RailElevationContext, useRailElevation } from './railElevationContext';
import { getPlaybackPosition } from '../../utils/simulationPlayback';
import { simulationClockStore } from '../../store/simulationClockStore';
import { playbackDelayStore } from '../../store/playbackDelayStore';
import { railTransferPoseStore } from '../../store/railTransferPoseStore';
import {
  computeCameraFocusBounds,
  computeSceneBounds,
  mergeFloorPlanBounds,
  padBoundsProportional,
  SCENE_TARGET_SIZE,
} from '../../utils/scene3dBounds';
import {
  computeSchematic3DCameraFitBounds,
  computeSchematic3DCameraFromViewport,
  getPerspectivePixelsPerWorldUnit,
  SCHEMATIC_3D_CAMERA_DIR,
  SCHEMATIC_3D_TARGET_Y,
} from '../../utils/flatPlanCamera';
import { computePolylineBBoxes, getDenseFitBounds } from '../../utils/floorplanHitTest';
import { getView3DOrbitProps } from '../../utils/view3dControls';
import { MIN_ZOOM, MAX_ZOOM } from '../../constants';
import { store } from '../../store';
import { viewportStore } from '../../store/viewportStore';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import type {
  FloorPlanBounds,
  LayoutElement,
  CameraViewPreset,
  FollowOhtCameraMode,
  TrackNodeAnnotation,
} from '../../types';
import { shouldReframeCameraOnSelection } from '../../utils/cameraReframePolicy';
import { getOhtCameraPose, lerpAngleDeg } from '../../utils/ohtCameraPose';
import { polygonBounds } from '../../utils/equipmentGeometry';
import {
  getElementFocusCenter,
  getFocusViewPose,
  getLayoutCameraLookHeight,
  getStandardViewPose,
  resolveFollowOhtId,
  sortOhtElements,
} from '../../utils/cameraViewPose';
import CameraViewMenu, { type CameraViewGroup } from './CameraViewMenu';
import {
  isElementVisible,
  isLayerHidden,
  computeLayoutBoundsFromElements,
} from '../../utils/layoutImport';
import SimulationFloorPlanTexture3D from './SimulationFloorPlanTexture3D';
import { hasVisibleDxfFloorGeometry } from '../../utils/floorRasterMode';
import CleanroomFloor from './CleanroomFloor';
import FabLighting from '../../lighting/FabLighting';
import { getFabVisualConfig } from '../../utils/fabVisualConfig';
import { FAB_CLEANROOM_FLOOR_OVER_DXF_OPACITY } from '../../utils/factory3dStyles';
import FabPostProcessing, { Canvas3DRenderBudget } from '../../postprocessing/FabPostProcessing';
import FabDebugSceneOverride from '../fab3d/FabDebugSceneOverride';
import FactoryElement3D from './FactoryElement3D';
import InstancedElements3D from './InstancedElements3D';
import SchematicElement3D from './SchematicElement3D';
import SimulationOverlay3D from './SimulationOverlay3D';
import RouteEndpointFlags3D from './RouteEndpointFlags3D';
import EquipmentLayer3D from './EquipmentLayer3D';
import DxfEquipmentInstances3D from './DxfEquipmentInstances3D';
import LayoutEquipmentLayer3D, { isEquipmentLayoutElement } from './LayoutEquipmentLayer3D';
import {
  Canvas3DLoadingOverlay,
  Scene3DReadyProbe,
  type Scene3DLoadingStage,
} from './Canvas3DLoadingOverlay';
import { selectElement } from '../../features/selection/selectionSlice';
import { InstancedTrack, InstancedJunction } from '../../3d/instancing';
import { AmhsKitTemplateCapture } from '../../3d/instancing/AmhsKitTemplateCapture';
import { ALL_RAIL_TYPES, isConnectorRailType } from '../../utils/topology';
import { FACTORY_3D_PALETTE } from '../../utils/factory3dStyles';
import {
  configureCanvas3DRenderer,
  getCanvas3DDprCap,
  getCanvas3DGlSettings,
  getCanvas3DLightBudget,
  getCanvas3DToneMapping,
  getCanvas3DToneMappingExposure,
} from '../../utils/canvas3dRenderer';
import type { Canvas3DEquipmentCounts } from './Canvas3DDiagnostics';

/** Types that previously used a dedicated equipment OBJ; now plain boxes via detailed meshes. */
const EQUIPMENT_BOX_TYPES = new Set(['stb', 'ohb', 'lifter']);
const LAYOUT_EQUIPMENT_TYPES = new Set<LayoutElement['type']>([
  'oht-vehicle',
  'stocker',
  'stb',
  'ohb',
  'lifter',
]);

const DEBUG_3D_ENABLED =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('debug3d') === '1';

const DevCanvas3DDiagnostics = React.lazy(() => import('./Canvas3DDiagnostics'));

/** Scene background — cleanroom white surround (#FFFFFF). */
const VIEW3D_BACKGROUND = FACTORY_3D_PALETTE.floor.surround;
/** Fab layout floor slab — light gray cleanroom floor (#B8C2CC). */
const VIEW3D_FLOOR_COLOR = FACTORY_3D_PALETTE.floor.surface;

/** White tiled fab slab — extends well beyond layout like the reference scene. */
function resolveFabFloorSize(rawW: number, rawH: number, spanHint?: number): { width: number; height: number } {
  const minSpan = SCENE_TARGET_SIZE * 1.55;
  const span = Math.max(spanHint ?? 0, minSpan);
  return {
    width: Math.max(rawW * 3.2, span * 1.35, minSpan),
    height: Math.max(rawH * 3.2, span * 1.35, minSpan),
  };
}
/** Keep scene + GL clear color white (WebGL defaults to black before init). */
function SceneBackground({ color = VIEW3D_BACKGROUND }: { color?: string }) {
  const { gl, scene } = useThree();
  useLayoutEffect(() => {
    const c = new THREE.Color(color);
    scene.background = c;
    gl.setClearColor(c, 1);
  }, [gl, scene, color]);
  return <color attach="background" args={[color]} />;
}

type OrbitControlsLike = {
  target: THREE.Vector3;
  update: () => void;
};

/** Live OHT pose for follow camera (runtime / playback — does not touch simulation). */
function resolveTrackedOhtKinematics(
  activeOhtElement: LayoutElement | null,
  activeOhtFrame: FrameVehicle | null
): { worldX: number; worldZ: number; headingDeg: number } | null {
  if (!activeOhtElement && !activeOhtFrame) return null;

  let worldX = activeOhtFrame ? activeOhtFrame.x : (activeOhtElement?.position.x ?? 0);
  let worldZ = activeOhtFrame ? activeOhtFrame.y : (activeOhtElement?.position.y ?? 0);
  let headingDeg = activeOhtFrame ? activeOhtFrame.angle : (activeOhtElement?.rotation ?? 0);

  const ohtId =
    activeOhtFrame?.ohtId ?? activeOhtFrame?.id ?? activeOhtElement?.id ?? null;
  if (ohtId) {
    const runtime = liveOhtRuntimeStore.getOht(ohtId);
    const pb = store.getState().simulation.playbackVehicles[ohtId];
    const idle =
      store.getState().simulation.browserKinetic?.vehicleLegs.idleByVehicle[ohtId];
    if (runtime) {
      worldX = runtime.x;
      worldZ = runtime.y;
      headingDeg = runtime.angle;
    } else if (pb && idle) {
      const t = playbackDelayStore.getEffectiveTime(ohtId, simulationClockStore.getTime());
      const pos = getPlaybackPosition(pb, idle, t, activeOhtFrame?.idleAngle);
      worldX = pos.x;
      worldZ = pos.y;
      headingDeg = pos.angle;
    }
  }

  return { worldX, worldZ, headingDeg };
}

function getPresetCameraPose({
  preset,
  layoutBounds,
  useCanvasSpace3D,
  bounds,
  selectedElement,
  selectedEquipmentFocus,
  activeOhtElement,
  activeOhtFrame,
  railElevation,
  followMode = 'chase',
  headingOverrideDeg,
}: {
  preset: CameraViewPreset;
  layoutBounds: FloorPlanBounds | null;
  useCanvasSpace3D: boolean;
  bounds: ReturnType<typeof computeSceneBounds>;
  selectedElement: LayoutElement | null;
  selectedEquipmentFocus: { x: number; y: number; z: number; span: number } | null;
  activeOhtElement: LayoutElement | null;
  activeOhtFrame: FrameVehicle | null;
  railElevation: number;
  followMode?: FollowOhtCameraMode;
  headingOverrideDeg?: number;
}): {
  target: [number, number, number];
  position: [number, number, number];
  fov?: number;
  minPolarAngle?: number;
  maxPolarAngle?: number;
} {
  let cx = 0;
  let cz = 0;
  let cy = 0;
  let span = 2000;

  if (useCanvasSpace3D) {
    cx = layoutBounds ? (layoutBounds.minX + layoutBounds.maxX) / 2 : 0;
    cz = layoutBounds ? (layoutBounds.minY + layoutBounds.maxY) / 2 : 0;
    cy = 0;
    const w = layoutBounds ? Math.max(layoutBounds.maxX - layoutBounds.minX, 100) : 2000;
    const h = layoutBounds ? Math.max(layoutBounds.maxY - layoutBounds.minY, 100) : 2000;
    span = Math.max(w, h, 200);
  } else {
    cx = 0;
    cz = 0;
    cy = SCENE_TARGET_SIZE * 0.06;
    span = SCENE_TARGET_SIZE;
  }

  const lookY = useCanvasSpace3D ? getLayoutCameraLookHeight(railElevation) : cy;
  const spanX = useCanvasSpace3D
    ? layoutBounds
      ? Math.max(layoutBounds.maxX - layoutBounds.minX, 200)
      : span
    : span;
  const spanZ = useCanvasSpace3D
    ? layoutBounds
      ? Math.max(layoutBounds.maxY - layoutBounds.minY, 200)
      : span
    : span;

  if (
    preset === 'default' ||
    preset === 'reference' ||
    preset === 'front45' ||
    preset === 'wide' ||
    preset === 'top' ||
    preset === 'front' ||
    preset === 'back' ||
    preset === 'left' ||
    preset === 'right'
  ) {
    const view = getStandardViewPose(preset, { cx, cz, lookY, spanX, spanZ });
    return {
      target: view.target,
      position: view.position,
      fov: view.fov,
      minPolarAngle: view.minPolarAngle,
      maxPolarAngle: view.maxPolarAngle,
    };
  }

  if (preset === 'focus' && (selectedElement || selectedEquipmentFocus)) {
    const scale = useCanvasSpace3D ? 1 : bounds.scale;
    const origin = useCanvasSpace3D ? { cx: 0, cz: 0 } : { cx: bounds.cx, cz: bounds.cz };
    const center = selectedElement
      ? getElementFocusCenter(selectedElement, railElevation, scale, origin)
      : selectedEquipmentFocus
        ? {
          x: selectedEquipmentFocus.x * scale - origin.cx,
          y: selectedEquipmentFocus.y * scale,
          z: selectedEquipmentFocus.z * scale - origin.cz,
          span: selectedEquipmentFocus.span * scale,
        }
        : null;
    if (center) {
      const focus = getFocusViewPose(center);
      return { target: focus.target, position: focus.position, fov: focus.fov };
    }
  }

  if (preset === 'follow' && (activeOhtElement || activeOhtFrame)) {
    const kin = resolveTrackedOhtKinematics(activeOhtElement, activeOhtFrame);
    if (!kin) {
      return {
        target: [cx, lookY, cz],
        position: [cx + span * 0.5, lookY + span * 0.6, cz + span * 0.5],
        fov: 52,
      };
    }

    const headingDeg = headingOverrideDeg ?? kin.headingDeg;
    const groupY = activeOhtFrame
      ? activeOhtFrame.groupY
      : activeOhtElement
        ? getElement3DGroupY(activeOhtElement, undefined, railElevation)
        : 50;

    const scale = useCanvasSpace3D ? 1 : bounds.scale;
    const vx = useCanvasSpace3D ? kin.worldX : kin.worldX * bounds.scale - bounds.cx;
    const vz = useCanvasSpace3D ? kin.worldZ : kin.worldZ * bounds.scale - bounds.cz;
    const vy = groupY * scale;
    const meshDepth = activeOhtFrame
      ? activeOhtFrame.meshDepth
      : activeOhtElement
        ? getElement3DExtrusionDepth(activeOhtElement)
        : 8;
    const bodyLength = activeOhtFrame
      ? Math.max(activeOhtFrame.w, activeOhtFrame.h)
      : activeOhtElement
        ? Math.max(activeOhtElement.dimensions.width, activeOhtElement.dimensions.height)
        : 20;
    const follow = getOhtCameraPose(
      'follow',
      {
        x: vx,
        y: vy,
        z: vz,
        headingDeg,
        meshDepth,
        bodyLength,
        scale,
      },
      followMode
    );

    return {
      target: follow.target,
      position: follow.position,
      fov: follow.fov,
    };
  }

  // Isometric / Default view (focus/follow fall through when their target is missing)
  const dist = Math.max(spanX, spanZ, span, 200) * 1.35;
  return {
    target: [cx, lookY, cz],
    position: [cx + dist * 0.55, lookY + dist * 0.72, cz + dist * 0.55],
  };
}

function AnimatedCameraRig({
  fitFullLayout,
  layoutBounds,
  dragLayout,
  onFitComplete,
  useCanvasSpace3D,
  bounds,
  preset,
  cameraPresetVersion,
  activeCameraOhtId,
  elements,
  selectedIds,
  simulationFrames,
}: {
  fitFullLayout: boolean;
  layoutBounds: FloorPlanBounds | null;
  dragLayout: boolean;
  onFitComplete: () => void;
  useCanvasSpace3D: boolean;
  bounds: ReturnType<typeof computeSceneBounds>;
  preset: CameraViewPreset;
  cameraPresetVersion?: number;
  activeCameraOhtId: string | null;
  elements: Record<string, LayoutElement>;
  selectedIds: string[];
  simulationFrames: FrameVehicle[];
}) {
  const [viewport] = useViewport();
  const { camera, size, invalidate } = useThree();
  const controlsRef = useRef<OrbitControlsLike | null>(null);
  const railElevation = useRailElevation();

  const selectedElement = selectedIds.length > 0 ? (elements[selectedIds[0]] ?? null) : null;
  const followOhtId = useAppSelector((s) => s.ui.active3DFollowOhtId);
  const followOhtMode = useAppSelector((s) => s.ui.active3DFollowOhtMode);
  const selectedEquipment = useAppSelector((s) => {
    const id = s.equipment.selectedInstanceId;
    return id ? (s.equipment.instances[id] ?? null) : null;
  });
  const selectedEquipmentFocus = useMemo(() => {
    if (!selectedEquipment?.polygon?.length) return null;
    const box = polygonBounds(selectedEquipment.polygon);
    return {
      x: (box.minX + box.maxX) / 2,
      y: EQUIPMENT_KIT_HEIGHT * 0.5,
      z: (box.minY + box.maxY) / 2,
      span: Math.max(box.maxX - box.minX, box.maxY - box.minY, 24),
    };
  }, [selectedEquipment]);

  const activeOhtElement = useMemo(() => {
    if (activeCameraOhtId && elements[activeCameraOhtId]?.type === 'oht-vehicle') {
      return elements[activeCameraOhtId];
    }
    const sortedIds = sortOhtElements(
      Object.values(elements).filter((el): el is LayoutElement => el?.type === 'oht-vehicle')
    ).map((el) => el.id);
    const followId = resolveFollowOhtId(
      sortedIds,
      selectedIds[0],
      (id) => elements[id]?.type === 'oht-vehicle',
      followOhtId
    );
    return followId ? elements[followId] : null;
  }, [activeCameraOhtId, elements, followOhtId, selectedIds]);

  const activeOhtFrame = useMemo(() => {
    if (!activeOhtElement) return null;
    return (
      simulationFrames.find(
        (f) => f.id === activeOhtElement.id || f.ohtId === activeOhtElement.id
      ) ?? null
    );
  }, [activeOhtElement, simulationFrames]);

  const targetPos = useRef(new THREE.Vector3());
  const targetLook = useRef(new THREE.Vector3());
  const isTransitioning = useRef(false);
  const isUserInteracting = useRef(false);
  const smoothFollowHeadingDeg = useRef(0);
  const smoothFollowHeadingReady = useRef(false);
  const lastUiZoomRef = useRef(viewport.zoom);
  const isZoomingFromUiRef = useRef(false);
  const debouncedSyncRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const spanVal =
    useCanvasSpace3D && layoutBounds
      ? Math.max(layoutBounds.maxX - layoutBounds.minX, layoutBounds.maxY - layoutBounds.minY, 200)
      : SCENE_TARGET_SIZE;

  const updatePoseTarget = useCallback(() => {
    const pose = getPresetCameraPose({
      preset,
      layoutBounds,
      useCanvasSpace3D,
      bounds,
      selectedElement,
      selectedEquipmentFocus,
      activeOhtElement,
      activeOhtFrame,
      railElevation,
      followMode: followOhtMode,
    });
    targetPos.current.set(pose.position[0], pose.position[1], pose.position[2]);
    targetLook.current.set(pose.target[0], pose.target[1], pose.target[2]);
    isTransitioning.current = true;
    isUserInteracting.current = false;
    smoothFollowHeadingReady.current = false;
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = pose.fov ?? 40;
      camera.near = useCanvasSpace3D
        ? Math.max(getCanvas3DGlSettings().logarithmicDepthBuffer ? 0.05 : 1, spanVal * 0.00015)
        : Math.max(getCanvas3DGlSettings().logarithmicDepthBuffer ? 0.5 : 1, 0.5);
      camera.far = useCanvasSpace3D ? spanVal * 30 : SCENE_TARGET_SIZE * 25;
      camera.updateProjectionMatrix();
    }
  }, [
    preset,
    layoutBounds,
    useCanvasSpace3D,
    bounds,
    selectedElement,
    selectedEquipmentFocus,
    activeOhtElement,
    activeOhtFrame,
    camera,
    spanVal,
    railElevation,
    followOhtMode,
  ]);
  const updatePoseTargetRef = useRef(updatePoseTarget);
  useEffect(() => {
    updatePoseTargetRef.current = updatePoseTarget;
  }, [updatePoseTarget]);

  // Initial/preset changes may reframe the camera. Ordinary selection changes
  // must not move an orbit camera: selecting, drawing, or editing equipment
  // previously restarted this transition and looked like an automatic zoom-out.
  useEffect(() => {
    updatePoseTargetRef.current();
  }, [preset, cameraPresetVersion]);

  // Selection drives the camera only when the user explicitly chose Focus.
  useEffect(() => {
    if (shouldReframeCameraOnSelection(preset)) updatePoseTargetRef.current();
  }, [preset, selectedElement?.id, selectedEquipment?.id]);

  // Respond to Fit button
  useEffect(() => {
    if (fitFullLayout) {
      updatePoseTargetRef.current();
      onFitComplete();
    }
  }, [fitFullLayout]);

  // Attach drag / orbit interaction listeners to OrbitControls
  useEffect(() => {
    const controls = controlsRef.current;
    if (
      !controls ||
      typeof (controls as unknown as Record<string, unknown>).addEventListener !== 'function'
    )
      return;
    const c = controls as unknown as {
      addEventListener: (t: string, fn: () => void) => void;
      removeEventListener: (t: string, fn: () => void) => void;
    };
    const onStart = () => {
      isUserInteracting.current = true;
      isTransitioning.current = false;
      cameraDragStore.setDragging(true);
    };
    const onEnd = () => {
      isUserInteracting.current = false;
      cameraDragStore.setDragging(false);
      invalidate();
    };
    c.addEventListener('start', onStart);
    c.addEventListener('end', onEnd);
    return () => {
      c.removeEventListener('start', onStart);
      c.removeEventListener('end', onEnd);
    };
  }, [controlsRef.current]);

  const syncZoomFromCamera = useCallback(() => {
    if (isZoomingFromUiRef.current) return;
    if (isTransitioning.current || isUserInteracting.current) return;
    if (!(camera instanceof THREE.PerspectiveCamera)) return;
    const controls = controlsRef.current;
    if (!controls) return;
    const h = size.height || viewport.canvasHeight || 800;
    const w = size.width || viewport.canvasWidth || 1200;
    const tx = controls.target.x;
    const tz = controls.target.z;
    const ty = controls.target.y;
    const rawPixels = getPerspectivePixelsPerWorldUnit(camera, h, tx, tz, ty);
    const scaleFactor = useCanvasSpace3D ? 1 : (bounds.scale || 1);
    const nextZoom = Math.max(
      MIN_ZOOM,
      Math.min(MAX_ZOOM, rawPixels * scaleFactor)
    );
    if (!Number.isFinite(nextZoom) || nextZoom < MIN_ZOOM * 1.01) return;
    if (Math.abs(nextZoom - viewportStore.getState().zoom) < 0.002) return;

    const panX = w / 2 - tx * nextZoom;
    const panY = h / 2 - tz * nextZoom;
    lastUiZoomRef.current = nextZoom;
    viewportStore.setState({ zoom: nextZoom, panX, panY });
    store.dispatch(setZoomLevel(nextZoom));
  }, [
    camera,
    useCanvasSpace3D,
    bounds.scale,
    size.height,
    size.width,
    viewport.canvasHeight,
    viewport.canvasWidth,
  ]);

  // Toolbar / Floating Canvas Menu +/- zoom buttons → drive 3D camera
  useEffect(() => {
    if (isUserInteracting.current) return;
    const currentUiZoom = viewport.zoom;
    const prevUiZoom = lastUiZoomRef.current;
    if (!Number.isFinite(currentUiZoom) || !Number.isFinite(prevUiZoom) || prevUiZoom <= 0) {
      lastUiZoomRef.current = currentUiZoom;
      return;
    }

    const ratio = currentUiZoom / prevUiZoom;
    if (Math.abs(ratio - 1) < 0.005) return;

    lastUiZoomRef.current = currentUiZoom;
    isZoomingFromUiRef.current = true;
    isTransitioning.current = false;

    const controls = controlsRef.current;
    if (controls) {
      if (camera instanceof THREE.PerspectiveCamera) {
        const target = controls.target;
        const offset = new THREE.Vector3().subVectors(camera.position, target);
        const currentDist = offset.length();
        if (currentDist > 0.001) {
          const minD = Math.max(1.5, spanVal * 0.0015);
          const maxD = Math.max(spanVal * 12, 10000);
          const nextDist = Math.max(minD, Math.min(maxD, currentDist / ratio));
          offset.setLength(nextDist);
          camera.position.copy(target).add(offset);
          targetPos.current.copy(camera.position);
          targetLook.current.copy(target);
          controls.update();
          invalidate();
        }
      } else if (camera instanceof THREE.OrthographicCamera) {
        camera.zoom = Math.max(0.01, camera.zoom * ratio);
        camera.updateProjectionMatrix();
        controls.update();
        invalidate();
      }
    }

    const timer = setTimeout(() => {
      isZoomingFromUiRef.current = false;
    }, 60);
    return () => clearTimeout(timer);
  }, [viewport.zoom, camera, invalidate, spanVal]);

  // Sync zoom percentage after framing or preset changes
  useEffect(() => {
    const timer = setTimeout(() => {
      syncZoomFromCamera();
    }, 150);
    return () => clearTimeout(timer);
  }, [preset, syncZoomFromCamera]);

  const orbitProps = useMemo(() => {
    const props = getView3DOrbitProps(spanVal, dragLayout, false, { topView: preset === 'top' });
    if (preset === 'top') {
      return props;
    }
    if (preset === 'front' || preset === 'back' || preset === 'left' || preset === 'right') {
      return {
        ...props,
        minPolarAngle: 0.85,
        maxPolarAngle: Math.PI / 2 - 0.04,
      };
    }
    if (preset === 'follow') {
      return {
        ...props,
        minPolarAngle: 0.35,
        maxPolarAngle: Math.PI / 2 - 0.12,
      };
    }
    return props;
  }, [spanVal, dragLayout, preset]);

  useFrame((_, delta) => {
    const controls = controlsRef.current;
    if (!controls) return;

    if (preset === 'follow' && (activeOhtElement || activeOhtFrame)) {
      const kin = resolveTrackedOhtKinematics(activeOhtElement, activeOhtFrame);
      if (kin) {
        if (!smoothFollowHeadingReady.current) {
          smoothFollowHeadingDeg.current = kin.headingDeg;
          smoothFollowHeadingReady.current = true;
        } else {
          smoothFollowHeadingDeg.current = lerpAngleDeg(
            smoothFollowHeadingDeg.current,
            kin.headingDeg,
            Math.min(1, delta * 4.5)
          );
        }
      }

      const pose = getPresetCameraPose({
        preset: 'follow',
        layoutBounds,
        useCanvasSpace3D,
        bounds,
        selectedElement,
        selectedEquipmentFocus,
        activeOhtElement,
        activeOhtFrame,
        railElevation,
        followMode: followOhtMode,
        headingOverrideDeg: smoothFollowHeadingReady.current
          ? smoothFollowHeadingDeg.current
          : kin?.headingDeg,
      });
      targetPos.current.set(pose.position[0], pose.position[1], pose.position[2]);
      targetLook.current.set(pose.target[0], pose.target[1], pose.target[2]);

      if (!isUserInteracting.current) {
        const posLerp = Math.min(1, delta * 5.5);
        const targetLerp = Math.min(1, delta * 6.5);
        camera.position.lerp(targetPos.current, posLerp);
        controls.target.lerp(targetLook.current, targetLerp);
        if (camera instanceof THREE.PerspectiveCamera) {
          const near = Math.max(
            getCanvas3DGlSettings().logarithmicDepthBuffer ? 0.35 : 0.8,
            spanVal * 0.00012
          );
          if (Math.abs(camera.near - near) > 0.05) {
            camera.near = near;
            camera.updateProjectionMatrix();
          }
          if (pose.fov && camera.fov !== pose.fov) {
            camera.fov = pose.fov;
            camera.updateProjectionMatrix();
          }
        }
        controls.update();
      }
      return;
    }

    if (isTransitioning.current && !isUserInteracting.current) {
      const lerpFactor = Math.min(1, delta * 6);
      camera.position.lerp(targetPos.current, lerpFactor);
      controls.target.lerp(targetLook.current, lerpFactor);
      controls.update();
      invalidate();

      if (
        camera.position.distanceTo(targetPos.current) < 0.2 &&
        controls.target.distanceTo(targetLook.current) < 0.2
      ) {
        isTransitioning.current = false;
        syncZoomFromCamera();
      }
    }
  });

  const handleOrbitChange = useCallback(() => {
    invalidate();
    if (debouncedSyncRef.current) clearTimeout(debouncedSyncRef.current);
    debouncedSyncRef.current = setTimeout(() => {
      debouncedSyncRef.current = null;
      syncZoomFromCamera();
    }, 100);
  }, [invalidate, syncZoomFromCamera]);

  useEffect(() => () => {
    if (debouncedSyncRef.current) clearTimeout(debouncedSyncRef.current);
  }, []);

  return (
    <OrbitControls
      ref={(node) => {
        controlsRef.current = node;
      }}
      {...orbitProps}
      enableRotate={!dragLayout}
      onChange={handleOrbitChange}
      onStart={() => cameraDragStore.setDragging(true)}
      onEnd={() => {
        cameraDragStore.setDragging(false);
        invalidate();
        queueMicrotask(syncZoomFromCamera);
      }}
    />
  );
}

export function SchematicCameraRig({
  fitFullLayout,
  layoutBounds,
  dragLayout,
  onFitComplete,
}: {
  fitFullLayout: boolean;
  layoutBounds: FloorPlanBounds | null;
  dragLayout: boolean;
  onFitComplete: () => void;
}) {
  const [viewport] = useViewport();
  const { camera, size, invalidate } = useThree();
  const controlsRef = useRef<OrbitControlsLike | null>(null);
  const spanRef = useRef(2000);
  const targetRef = useRef<[number, number, number]>([0, 0, 0]);
  /** Skip orbit→UI writeback while applying toolbar / fit camera moves. */
  const applyingFromUiRef = useRef(false);
  const lastUiZoomRef = useRef(viewport.zoom);
  const lastUiPanRef = useRef({ x: viewport.panX, y: viewport.panY });
  /** Ignore stale 2D zoom/pan right after a programmatic fit. */
  const ignoreViewportSyncUntilRef = useRef(0);

  // Clear any leftover multi-view crop from earlier fit experiments.
  useLayoutEffect(() => {
    if (!(camera instanceof THREE.PerspectiveCamera)) return;
    camera.clearViewOffset();
    camera.updateProjectionMatrix();
  }, [camera]);

  const syncViewportFromCamera = useCallback(
    (tx: number, ty: number, tz: number) => {
      if (!(camera instanceof THREE.PerspectiveCamera)) return;
      const h = size.height || viewport.canvasHeight || 800;
      const w = size.width || viewport.canvasWidth || 1200;
      const nextZoom = Math.max(
        MIN_ZOOM,
        Math.min(MAX_ZOOM, getPerspectivePixelsPerWorldUnit(camera, h, tx, tz, ty))
      );
      if (!Number.isFinite(nextZoom) || nextZoom < MIN_ZOOM * 1.01) return;
      const panX = w / 2 - tx * nextZoom;
      const panY = h / 2 - tz * nextZoom;
      lastUiZoomRef.current = nextZoom;
      lastUiPanRef.current = { x: panX, y: panY };
      ignoreViewportSyncUntilRef.current = performance.now() + 250;
      viewportStore.setState({ zoom: nextZoom, panX, panY });
      store.dispatch(setZoomLevel(nextZoom));
    },
    [camera, size.height, size.width, viewport.canvasHeight, viewport.canvasWidth]
  );

  const applyCameraFit = useCallback(
    (fullLayout: boolean) => {
      const w = size.width || viewport.canvasWidth || 1200;
      const h = size.height || viewport.canvasHeight || 800;
      const vp = { ...viewport, canvasWidth: w, canvasHeight: h };

      const fit =
        fullLayout && layoutBounds
          ? computeSchematic3DCameraFitBounds(layoutBounds, w, h)
          : layoutBounds
            ? computeSchematic3DCameraFromViewport(vp, layoutBounds)
            : computeSchematic3DCameraFromViewport(vp, {
              minX: -1000,
              minY: -1000,
              maxX: 1000,
              maxY: 1000,
            });

      applyingFromUiRef.current = true;
      spanRef.current = fit.span;
      targetRef.current = [fit.cx, fit.targetY, fit.cz];
      camera.position.set(fit.position[0], fit.position[1], fit.position[2]);
      if (controlsRef.current) {
        controlsRef.current.target.set(fit.cx, fit.targetY, fit.cz);
        controlsRef.current.update();
      } else {
        camera.lookAt(fit.cx, fit.targetY, fit.cz);
      }
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.clearViewOffset();
        camera.near = Math.max(
          getCanvas3DGlSettings().logarithmicDepthBuffer ? 0.05 : 1,
          fit.span * 0.00015
        );
        camera.far = fit.span * 30;
        camera.updateProjectionMatrix();
      }
      // Keep the zoom % / pan in sync with the new framing (avoid stale 700% from 2D).
      syncViewportFromCamera(fit.cx, fit.targetY, fit.cz);
      invalidate();
      requestAnimationFrame(() => {
        applyingFromUiRef.current = false;
      });
    },
    [camera, invalidate, layoutBounds, size.height, size.width, syncViewportFromCamera, viewport]
  );

  const hasFittedRef = useRef(false);

  // Initial camera alignment once layoutBounds becomes available
  useEffect(() => {
    if (!hasFittedRef.current && layoutBounds) {
      applyCameraFit(true);
      hasFittedRef.current = true;
      lastUiZoomRef.current = viewport.zoom;
      lastUiPanRef.current = { x: viewport.panX, y: viewport.panY };
    }
  }, [applyCameraFit, layoutBounds]);

  // Fit button
  useEffect(() => {
    if (fitFullLayout) {
      applyCameraFit(true);
      onFitComplete();
    }
  }, [applyCameraFit, fitFullLayout, onFitComplete]);

  // Toolbar +/- zoom (and 2D pan) → drive the 3D camera so the % isn't stuck at fit.
  useEffect(() => {
    if (!hasFittedRef.current || fitFullLayout) return;
    if (performance.now() < ignoreViewportSyncUntilRef.current) return;
    const zoomChanged = Math.abs(viewport.zoom - lastUiZoomRef.current) > 1e-6;
    const panChanged =
      Math.abs(viewport.panX - lastUiPanRef.current.x) > 1e-3 ||
      Math.abs(viewport.panY - lastUiPanRef.current.y) > 1e-3;
    if (!zoomChanged && !panChanged) return;
    lastUiZoomRef.current = viewport.zoom;
    lastUiPanRef.current = { x: viewport.panX, y: viewport.panY };
    applyCameraFit(false);
  }, [applyCameraFit, fitFullLayout, viewport.panX, viewport.panY, viewport.zoom]);

  const syncZoomFromCamera = useCallback(() => {
    if (applyingFromUiRef.current) return;
    if (!(camera instanceof THREE.PerspectiveCamera)) return;
    const controls = controlsRef.current;
    if (!controls) return;
    syncViewportFromCamera(controls.target.x, controls.target.y, controls.target.z);
    invalidate();
  }, [camera, syncViewportFromCamera, invalidate]);

  return (
    <OrbitControls
      ref={(node) => {
        controlsRef.current = node;
      }}
      {...getView3DOrbitProps(spanRef.current, dragLayout)}
      enableRotate={!dragLayout}
      target={targetRef.current}
      onChange={() => invalidate()}
      onStart={() => cameraDragStore.setDragging(true)}
      onEnd={() => {
        cameraDragStore.setDragging(false);
        invalidate();
        syncZoomFromCamera();
      }}
    />
  );
}

export function CameraRig({
  fitFullLayout,
  dragLayout,
  onFitComplete,
}: {
  fitFullLayout: boolean;
  dragLayout: boolean;
  onFitComplete: () => void;
}) {
  const { camera, invalidate } = useThree();
  const controlsRef = useRef<OrbitControlsLike | null>(null);

  const fitCamera = useCallback(() => {
    const dist = SCENE_TARGET_SIZE * 1.35;
    camera.position.set(dist * 0.55, dist * 0.72, dist * 0.55);
    if (controlsRef.current) {
      controlsRef.current.target.set(0, SCENE_TARGET_SIZE * 0.06, 0);
      controlsRef.current.update();
    } else {
      camera.lookAt(0, SCENE_TARGET_SIZE * 0.06, 0);
    }
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.near = getCanvas3DGlSettings().logarithmicDepthBuffer ? 0.5 : 1;
      camera.far = SCENE_TARGET_SIZE * 25;
      camera.updateProjectionMatrix();
    }
    invalidate();
  }, [camera, invalidate]);

  const hasFittedRef = useRef(false);

  // Perform initial camera alignment once
  useEffect(() => {
    if (!hasFittedRef.current) {
      fitCamera();
      hasFittedRef.current = true;
    }
  }, [fitCamera]);

  // Perform subsequent fit requests from the parent button
  useEffect(() => {
    if (fitFullLayout) {
      fitCamera();
      onFitComplete();
    }
  }, [fitCamera, fitFullLayout, onFitComplete]);

  return (
    <OrbitControls
      ref={(node) => {
        controlsRef.current = node;
      }}
      {...getView3DOrbitProps(SCENE_TARGET_SIZE, dragLayout)}
      target={[0, SCENE_TARGET_SIZE * 0.06, 0]}
      onChange={() => invalidate()}
      onStart={() => cameraDragStore.setDragging(true)}
      onEnd={() => {
        cameraDragStore.setDragging(false);
        invalidate();
      }}
    />
  );
}

/**
 * Monitors user interaction on the WebGL canvas (pointer drag, mouse wheel zoom,
 * and per-frame camera movement) to keep cameraDragStore accurately synchronized.
 */
function CameraInteractionMonitor() {
  const gl = useThree((s) => s.gl);
  const camera = useThree((s) => s.camera);
  const lastPos = useRef<THREE.Vector3 | null>(null);
  const lastRot = useRef<THREE.Quaternion | null>(null);

  useEffect(() => {
    const el = gl?.domElement;
    if (!el) return;

    const onWheel = () => {
      cameraDragStore.notifyWheel();
    };
    const onPointerDown = () => {
      cameraDragStore.setDragging(true);
    };
    const onPointerUp = () => {
      cameraDragStore.setDragging(false);
    };

    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [gl]);

  useFrame(() => {
    if (!lastPos.current) {
      lastPos.current = camera.position.clone();
      lastRot.current = camera.quaternion.clone();
      return;
    }
    const posDelta = camera.position.distanceTo(lastPos.current);
    const rotDelta = camera.quaternion.angleTo(lastRot.current!);
    lastPos.current.copy(camera.position);
    lastRot.current!.copy(camera.quaternion);

    if (posDelta > 0.05 || rotDelta > 0.002) {
      cameraDragStore.notifyCameraMotion();
    }
  });

  return null;
}

/**
 * Locks the 3D camera to a single OHT's onboard viewpoint — at the underside
 * of the track, looking forward so the hanging vehicle fills the lower half
 * of the frame. Uses the live simulation clock when running.
 */
function OHTCameraRig({
  element,
  frame,
  useCanvasSpace3D,
  bounds,
}: {
  element: LayoutElement;
  frame: FrameVehicle | null;
  useCanvasSpace3D: boolean;
  bounds: ReturnType<typeof computeSceneBounds>;
}) {
  const { camera } = useThree();
  const railElevation = useRailElevation();
  const playback = useAppSelector((state) =>
    frame ? state.simulation.playbackVehicles[frame.ohtId] : undefined
  );
  const idle = useAppSelector((state) =>
    frame ? state.simulation.browserKinetic?.vehicleLegs.idleByVehicle[frame.ohtId] : undefined
  );

  useFrame(() => {
    let worldX = frame ? frame.x : element.position.x;
    let worldZ = frame ? frame.y : element.position.y;
    let headingDeg = frame ? frame.angle : element.rotation;
    const groupY = frame ? frame.groupY : getElement3DGroupY(element, undefined, railElevation);
    const depth = frame ? frame.meshDepth : getElement3DExtrusionDepth(element);

    const runtime = frame ? liveOhtRuntimeStore.getOht(frame.ohtId) : null;
    if (runtime) {
      worldX = runtime.x;
      worldZ = runtime.y;
      headingDeg = runtime.angle;
    } else if (frame && playback && idle) {
      const t = playbackDelayStore.getEffectiveTime(frame.ohtId, simulationClockStore.getTime());
      const position = getPlaybackPosition(playback, idle, t, frame.idleAngle);
      worldX = position.x;
      worldZ = position.y;
      headingDeg = position.angle;
    }

    if (frame) {
      const override =
        railTransferPoseStore.get(frame.ohtId) ?? railTransferPoseStore.get(frame.id);
      if (override) {
        worldX = override.x;
        worldZ = override.y;
        headingDeg = override.angle;
      }
    }

    const scale = useCanvasSpace3D ? 1 : bounds.scale;
    const x = useCanvasSpace3D ? worldX : worldX * bounds.scale - bounds.cx;
    const y = groupY * scale;
    const z = useCanvasSpace3D ? worldZ : worldZ * bounds.scale - bounds.cz;
    const bodyLength = frame
      ? Math.max(frame.w, frame.h)
      : Math.max(element.dimensions.width, element.dimensions.height);
    const pose = getOhtCameraPose('onboard', {
      x,
      y,
      z,
      headingDeg,
      meshDepth: depth,
      bodyLength,
      scale,
    });

    camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
    camera.lookAt(pose.target[0], pose.target[1], pose.target[2]);
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = pose.fov;
      camera.near = getCanvas3DGlSettings().logarithmicDepthBuffer ? 0.05 : 0.5;
      camera.far = useCanvasSpace3D ? 50000 : SCENE_TARGET_SIZE * 25;
      camera.updateProjectionMatrix();
    }
  });

  return null;
}

interface ProcessedRailInfo {
  element: LayoutElement;
  minWorldX: number;
  maxWorldX: number;
  minWorldZ: number;
  maxWorldZ: number;
  centerWorldX: number;
  centerWorldZ: number;
  isHorizontal: boolean;
}

/**
 * Compute rod positions along a rail span, ensuring rods are inset from the
 * extreme start and end of the rail so clamp brackets sit 100% on the track
 * and never overhang open track ends.
 */
function calculateInsetRodPositions(
  minVal: number,
  maxVal: number,
  rodTargetStep = 85.0,
  minInset = 14.0
): number[] {
  const span = maxVal - minVal;
  if (span <= 0) return [];

  if (span < 45) {
    return [(minVal + maxVal) / 2];
  }

  const inset = Math.min(minInset, span * 0.15);
  const start = minVal + inset;
  const end = maxVal - inset;
  const innerSpan = end - start;
  if (innerSpan <= 0) {
    return [(minVal + maxVal) / 2];
  }

  const count = Math.max(2, Math.round(innerSpan / rodTargetStep) + 1);
  const step = innerSpan / (count - 1);
  const positions: number[] = [];
  for (let i = 0; i < count; i++) {
    positions.push(start + i * step);
  }
  return positions;
}

interface UnifiedGantrySystem3DProps {
  elements: Record<string, LayoutElement>;
  opacity: number;
}

/** Light silver-gray structural columns / gantry, with soft silver hardware. */
const GANTRY_WHITE = '#B8BDC1';
const GANTRY_WHITE_SOFT = '#A7B0B8';
const GANTRY_WHITE_EDGE = '#9BA5AE';
const GANTRY_MAT = { metalness: 0.8, roughness: 0.32 } as const;
const GANTRY_MAT_SOFT = { metalness: 0.72, roughness: 0.38 } as const;

const UnifiedGantrySystem3D: React.FC<UnifiedGantrySystem3DProps> = ({ elements, opacity }) => {
  // The frame hangs off the rails, so it has to rise with them.
  const railElevation = useRailElevation();
  const gantryTopY = 15.5; // gantry frame height above rail


  // 1. Gather all straight overhead rails and project into 3D world space
  const processedStraightRails = useMemo(() => {
    return Object.values(elements)
      .filter((el) => {
        if (el.type !== 'overhead-rail' && el.type !== 'cross-rail') return false;
        const railType = resolveRailType(el);
        return !isCurveRailType(railType);
      })
      .map((el): ProcessedRailInfo => {
        const rotY = -((el.rotation || 0) * Math.PI) / 180;
        const cos = Math.cos(rotY);
        const sin = Math.sin(rotY);

        const w = el.dimensions.width;
        const h = el.dimensions.height;

        let lxStart = 0,
          lzStart = 0,
          lxEnd = 0,
          lzEnd = 0;
        if (w >= h) {
          lxStart = 0;
          lzStart = h / 2;
          lxEnd = w;
          lzEnd = h / 2;
        } else {
          lxStart = w / 2;
          lzStart = 0;
          lxEnd = w / 2;
          lzEnd = h;
        }

        const wsx = el.position.x + (lxStart * cos + lzStart * sin);
        const wsz = el.position.y + (-lxStart * sin + lzStart * cos);

        const wex = el.position.x + (lxEnd * cos + lzEnd * sin);
        const wez = el.position.y + (-lxEnd * sin + lzEnd * cos);

        const minWorldX = Math.min(wsx, wex);
        const maxWorldX = Math.max(wsx, wex);
        const minWorldZ = Math.min(wsz, wez);
        const maxWorldZ = Math.max(wsz, wez);

        const spanX = maxWorldX - minWorldX;
        const spanZ = maxWorldZ - minWorldZ;

        const isHorizontal = spanX >= spanZ;
        const centerWorldX = (wsx + wex) / 2;
        const centerWorldZ = (wsz + wez) / 2;

        return {
          element: el,
          minWorldX,
          maxWorldX,
          minWorldZ,
          maxWorldZ,
          centerWorldX,
          centerWorldZ,
          isHorizontal,
        };
      });
  }, [elements]);

  // 2. Separate into horizontal and vertical straight rails in 3D world space
  const { horizontalRails, verticalRails } = useMemo(() => {
    const horiz: ProcessedRailInfo[] = [];
    const vert: ProcessedRailInfo[] = [];
    processedStraightRails.forEach((r) => {
      if (r.isHorizontal) {
        horiz.push(r);
      } else {
        vert.push(r);
      }
    });
    return { horizontalRails: horiz, verticalRails: vert };
  }, [processedStraightRails]);

  // 3. Cluster horizontal rails close to each other along world Z and overlapping on world X
  const horizontalClusters = useMemo(() => {
    const clusters: ProcessedRailInfo[][] = [];
    const visited = new Set<string>();

    horizontalRails.forEach((r) => {
      if (visited.has(r.element.id)) return;
      const cluster: ProcessedRailInfo[] = [];
      const queue = [r];
      visited.add(r.element.id);

      while (queue.length > 0) {
        const current = queue.shift()!;
        cluster.push(current);

        horizontalRails.forEach((other) => {
          if (visited.has(other.element.id)) return;

          const zDistance = Math.abs(current.centerWorldZ - other.centerWorldZ);
          const xOverlap =
            Math.max(current.minWorldX, other.minWorldX) <
            Math.min(current.maxWorldX, other.maxWorldX) + 10.0;

          if (zDistance <= 80.0 && xOverlap) {
            visited.add(other.element.id);
            queue.push(other);
          }
        });
      }
      clusters.push(cluster);
    });
    return clusters;
  }, [horizontalRails]);

  // 4. Cluster vertical rails close to each other along world X and overlapping on world Z
  const verticalClusters = useMemo(() => {
    const clusters: ProcessedRailInfo[][] = [];
    const visited = new Set<string>();

    verticalRails.forEach((r) => {
      if (visited.has(r.element.id)) return;
      const cluster: ProcessedRailInfo[] = [];
      const queue = [r];
      visited.add(r.element.id);

      while (queue.length > 0) {
        const current = queue.shift()!;
        cluster.push(current);

        verticalRails.forEach((other) => {
          if (visited.has(other.element.id)) return;

          const xDistance = Math.abs(current.centerWorldX - other.centerWorldX);
          const zOverlap =
            Math.max(current.minWorldZ, other.minWorldZ) <
            Math.min(current.maxWorldZ, other.maxWorldZ) + 10.0;

          if (xDistance <= 80.0 && zOverlap) {
            visited.add(other.element.id);
            queue.push(other);
          }
        });
      }
      clusters.push(cluster);
    });
    return clusters;
  }, [verticalRails]);

  // Helper to calculate exact arch X coordinates for horizontal clusters
  const getHorizontalClusterArchCoords = useCallback(
    (cluster: ProcessedRailInfo[]) => {
      const minX = Math.min(...cluster.map((r) => r.minWorldX));
      const maxX = Math.max(...cluster.map((r) => r.maxWorldX));
      const centerlinesZ = cluster.map((r) => r.centerWorldZ);
      const minZ = Math.min(...centerlinesZ);
      const maxZ = Math.max(...centerlinesZ);

      let extMinX = minX;
      let extMaxX = maxX;

      const junctionXCoords: number[] = [];

      verticalRails.forEach((v) => {
        if (v.minWorldZ <= maxZ + 50.0 && v.maxWorldZ >= minZ - 50.0) {
          if (Math.abs(v.centerWorldX - minX) < 50.0) {
            extMinX = Math.min(extMinX, v.centerWorldX - 40.0);
          }
          if (Math.abs(v.centerWorldX - maxX) < 50.0) {
            extMaxX = Math.max(extMaxX, v.centerWorldX + 40.0);
          }

          if (v.centerWorldX >= extMinX - 10.0 && v.centerWorldX <= extMaxX + 10.0) {
            junctionXCoords.push(v.centerWorldX);
          }
        }
      });

      const sortedRaw = Array.from(new Set([extMinX, extMaxX, ...junctionXCoords])).sort(
        (a, b) => a - b
      );

      // Merge arch coordinates that are within 100.0 units of each other to prevent overcrowding
      const rawCoords: number[] = [];
      sortedRaw.forEach((x) => {
        if (rawCoords.length === 0) {
          rawCoords.push(x);
        } else {
          const prev = rawCoords[rawCoords.length - 1];
          if (x === extMaxX || x - prev >= 100.0) {
            if (x === extMaxX && x - prev < 60.0 && rawCoords.length > 1) {
              rawCoords[rawCoords.length - 1] = x;
            } else {
              rawCoords.push(x);
            }
          }
        }
      });

      const finalCoords: number[] = [];
      const maxSegmentLength = 240.0;

      for (let i = 0; i < rawCoords.length - 1; i++) {
        const start = rawCoords[i];
        const end = rawCoords[i + 1];
        finalCoords.push(start);

        const dist = end - start;
        if (dist > maxSegmentLength) {
          const numSegments = Math.ceil(dist / maxSegmentLength);
          const step = dist / numSegments;
          for (let s = 1; s < numSegments; s++) {
            finalCoords.push(start + s * step);
          }
        }
      }
      if (rawCoords.length > 0) {
        finalCoords.push(rawCoords[rawCoords.length - 1]);
      }

      return {
        extMinX,
        extMaxX,
        minZ,
        maxZ,
        archCoords: Array.from(new Set(finalCoords)).sort((a, b) => a - b),
      };
    },
    [verticalRails]
  );

  // Helper to calculate exact arch Z coordinates for vertical clusters
  const getVerticalClusterArchCoords = useCallback(
    (cluster: ProcessedRailInfo[]) => {
      const minZ = Math.min(...cluster.map((r) => r.minWorldZ));
      const maxZ = Math.max(...cluster.map((r) => r.maxWorldZ));
      const centerlinesX = cluster.map((r) => r.centerWorldX);
      const minX = Math.min(...centerlinesX);
      const maxX = Math.max(...centerlinesX);

      let extMinZ = minZ;
      let extMaxZ = maxZ;

      const junctionZCoords: number[] = [];

      horizontalRails.forEach((h) => {
        if (h.minWorldX <= maxX + 50.0 && h.maxWorldX >= minX - 50.0) {
          if (Math.abs(h.centerWorldZ - minZ) < 50.0) {
            extMinZ = Math.min(extMinZ, h.centerWorldZ - 40.0);
          }
          if (Math.abs(h.centerWorldZ - maxZ) < 50.0) {
            extMaxZ = Math.max(extMaxZ, h.centerWorldZ + 40.0);
          }

          if (h.centerWorldZ >= extMinZ - 10.0 && h.centerWorldZ <= extMaxZ + 10.0) {
            junctionZCoords.push(h.centerWorldZ);
          }
        }
      });

      const sortedRaw = Array.from(new Set([extMinZ, extMaxZ, ...junctionZCoords])).sort(
        (a, b) => a - b
      );

      // Merge arch coordinates that are within 100.0 units of each other to prevent overcrowding
      const rawCoords: number[] = [];
      sortedRaw.forEach((z) => {
        if (rawCoords.length === 0) {
          rawCoords.push(z);
        } else {
          const prev = rawCoords[rawCoords.length - 1];
          if (z === extMaxZ || z - prev >= 100.0) {
            if (z === extMaxZ && z - prev < 60.0 && rawCoords.length > 1) {
              rawCoords[rawCoords.length - 1] = z;
            } else {
              rawCoords.push(z);
            }
          }
        }
      });

      const finalCoords: number[] = [];
      const maxSegmentLength = 240.0;

      for (let i = 0; i < rawCoords.length - 1; i++) {
        const start = rawCoords[i];
        const end = rawCoords[i + 1];
        finalCoords.push(start);

        const dist = end - start;
        if (dist > maxSegmentLength) {
          const numSegments = Math.ceil(dist / maxSegmentLength);
          const step = dist / numSegments;
          for (let s = 1; s < numSegments; s++) {
            finalCoords.push(start + s * step);
          }
        }
      }
      if (rawCoords.length > 0) {
        finalCoords.push(rawCoords[rawCoords.length - 1]);
      }

      return {
        extMinZ,
        extMaxZ,
        minX,
        maxX,
        archCoords: Array.from(new Set(finalCoords)).sort((a, b) => a - b),
      };
    },
    [horizontalRails]
  );

  // 5. Outer Boundary & Interconnection Structural Grid
  const outerGrid = useMemo(() => {
    let innerMinX = Infinity,
      innerMaxX = -Infinity,
      innerMinZ = Infinity,
      innerMaxZ = -Infinity;

    horizontalClusters.forEach((c) => {
      const { extMinX, extMaxX, minZ, maxZ } = getHorizontalClusterArchCoords(c);
      innerMinX = Math.min(innerMinX, extMinX);
      innerMaxX = Math.max(innerMaxX, extMaxX);
      innerMinZ = Math.min(innerMinZ, minZ - 40.0);
      innerMaxZ = Math.max(innerMaxZ, maxZ + 40.0);
    });

    verticalClusters.forEach((c) => {
      const { extMinZ, extMaxZ, minX, maxX } = getVerticalClusterArchCoords(c);
      innerMinX = Math.min(innerMinX, minX - 40.0);
      innerMaxX = Math.max(innerMaxX, maxX + 40.0);
      innerMinZ = Math.min(innerMinZ, extMinZ);
      innerMaxZ = Math.max(innerMaxZ, extMaxZ);
    });

    if (!isFinite(innerMinX) || !isFinite(innerMaxX)) {
      return null;
    }

    const outerBeamsAndPillars = Object.values(elements).filter(
      (el) => el.type === 'horizontal-beam' || el.type === 'support-pillar'
    );

    let calcOuterMinX = Infinity,
      calcOuterMaxX = -Infinity,
      calcOuterMinZ = Infinity,
      calcOuterMaxZ = -Infinity;

    outerBeamsAndPillars.forEach((el) => {
      const rot = -((el.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(rot);
      const sin = Math.sin(rot);
      const w = el.dimensions?.width ?? 4;
      const h = el.dimensions?.height ?? 4;
      const corners = [
        [0, 0],
        [w, 0],
        [0, h],
        [w, h],
      ];
      corners.forEach(([cx, cy]) => {
        const wx = el.position.x + (cx * cos + cy * sin);
        const wz = el.position.y + (-cx * sin + cy * cos);
        calcOuterMinX = Math.min(calcOuterMinX, wx);
        calcOuterMaxX = Math.max(calcOuterMaxX, wx);
        calcOuterMinZ = Math.min(calcOuterMinZ, wz);
        calcOuterMaxZ = Math.max(calcOuterMaxZ, wz);
      });
    });

    const DEFAULT_MARGIN = 95.0;
    const outerMinX =
      isFinite(calcOuterMinX) && calcOuterMinX < innerMinX - 10
        ? calcOuterMinX
        : innerMinX - DEFAULT_MARGIN;
    const outerMaxX =
      isFinite(calcOuterMaxX) && calcOuterMaxX > innerMaxX + 10
        ? calcOuterMaxX
        : innerMaxX + DEFAULT_MARGIN;
    const outerMinZ =
      isFinite(calcOuterMinZ) && calcOuterMinZ < innerMinZ - 10
        ? calcOuterMinZ
        : innerMinZ - DEFAULT_MARGIN;
    const outerMaxZ =
      isFinite(calcOuterMaxZ) && calcOuterMaxZ > innerMaxZ + 10
        ? calcOuterMaxZ
        : innerMaxZ + DEFAULT_MARGIN;

    const xLeft = (outerMinX + innerMinX) / 2;
    const xRight = (innerMaxX + outerMaxX) / 2;
    const zTop = (outerMinZ + innerMinZ) / 2;
    const zBottom = (innerMaxZ + outerMaxZ) / 2;

    const defaultXMid = (innerMinX + innerMaxX) / 2;
    const defaultZMid = (innerMinZ + innerMaxZ) / 2;

    const xMidCandidates: number[] = [];
    horizontalClusters.forEach((c) => {
      const { archCoords } = getHorizontalClusterArchCoords(c);
      archCoords.forEach((ax) => xMidCandidates.push(ax));
    });
    const zMidCandidates: number[] = [];
    verticalClusters.forEach((c) => {
      const { archCoords } = getVerticalClusterArchCoords(c);
      archCoords.forEach((az) => zMidCandidates.push(az));
    });

    const xMid =
      xMidCandidates.length > 0
        ? xMidCandidates.reduce((prev, curr) =>
          Math.abs(curr - defaultXMid) < Math.abs(prev - defaultXMid) ? curr : prev
        )
        : defaultXMid;

    const zMid =
      zMidCandidates.length > 0
        ? zMidCandidates.reduce((prev, curr) =>
          Math.abs(curr - defaultZMid) < Math.abs(prev - defaultZMid) ? curr : prev
        )
        : defaultZMid;

    const gantryBeamY = railElevation + gantryTopY;

    return {
      innerMinX,
      innerMaxX,
      innerMinZ,
      innerMaxZ,
      outerMinX,
      outerMaxX,
      outerMinZ,
      outerMaxZ,
      xLeft,
      xMid,
      xRight,
      zTop,
      zMid,
      zBottom,
      gantryBeamY,
    };
  }, [
    elements,
    horizontalClusters,
    verticalClusters,
    getHorizontalClusterArchCoords,
    getVerticalClusterArchCoords,
    railElevation,
    gantryTopY,
  ]);


  // 7. Gather curve rails only — straight/junction rails get their overhead
  // frame from the cluster pass, so including them here double-hangs them.
  const overheadCurveElements = useMemo(() => {
    return Object.values(elements).filter((el) => {
      if (el.type !== 'overhead-rail' && el.type !== 'overhead-curve' && el.type !== 'cross-rail')
        return false;
      return isCurveRailType(resolveRailType(el));
    });
  }, [elements]);

  // Junction center coordinates to prevent placing suspension rods across turntables
  const junctionCenters = useMemo(() => {
    return Object.values(elements)
      .filter((el) => isJunctionRailType(resolveRailType(el)))
      .map((el) => ({
        x: el.position.x + el.dimensions.width / 2,
        z: el.position.y + el.dimensions.height / 2,
      }));
  }, [elements]);

  // 8. Outer Boundary & Center Frame Connecting Gantry Beams
  const outerConnectingBeams = useMemo(() => {
    if (!outerGrid) return [];
    const {
      innerMinX,
      innerMaxX,
      innerMinZ,
      innerMaxZ,
      outerMinX,
      outerMaxX,
      outerMinZ,
      outerMaxZ,
      xLeft,
      xMid,
      xRight,
      zTop,
      zMid,
      zBottom,
      gantryBeamY,
    } = outerGrid;

    const beams: Array<{
      key: string;
      position: [number, number, number];
      size: [number, number, number];
    }> = [];

    const BEAM_THICKNESS = 1.4;
    const BEAM_HEIGHT = 1.2;

    // ──────────────── 1. HORIZONTAL GRID BEAMS (Along World X) ────────────────
    // Top Horizontal Beam: runs fully from outer left to outer right
    const fullSpanX = Math.max(1, outerMaxX - outerMinX);
    beams.push({
      key: 'grid-beam-h-top',
      position: [(outerMinX + outerMaxX) / 2, gantryBeamY, zTop],
      size: [fullSpanX, BEAM_HEIGHT, BEAM_THICKNESS],
    });

    // Middle Horizontal Left Beam: from outer left to inner frame left
    if (innerMinX > outerMinX + 1.0) {
      const spanX = innerMinX - outerMinX;
      beams.push({
        key: 'grid-beam-h-mid-left',
        position: [(outerMinX + innerMinX) / 2, gantryBeamY, zMid],
        size: [spanX, BEAM_HEIGHT, BEAM_THICKNESS],
      });
    }

    // Middle Horizontal Right Beam: from inner frame right to outer right
    if (outerMaxX > innerMaxX + 1.0) {
      const spanX = outerMaxX - innerMaxX;
      beams.push({
        key: 'grid-beam-h-mid-right',
        position: [(innerMaxX + outerMaxX) / 2, gantryBeamY, zMid],
        size: [spanX, BEAM_HEIGHT, BEAM_THICKNESS],
      });
    }

    // Bottom Horizontal Beam: runs fully from outer left to outer right
    beams.push({
      key: 'grid-beam-h-bottom',
      position: [(outerMinX + outerMaxX) / 2, gantryBeamY, zBottom],
      size: [fullSpanX, BEAM_HEIGHT, BEAM_THICKNESS],
    });

    // ──────────────── 2. VERTICAL GRID BEAMS (Along World Z) ────────────────
    // Left Vertical Beam: runs fully from outer top to outer bottom
    const fullSpanZ = Math.max(1, outerMaxZ - outerMinZ);
    beams.push({
      key: 'grid-beam-v-left',
      position: [xLeft, gantryBeamY, (outerMinZ + outerMaxZ) / 2],
      size: [BEAM_THICKNESS, BEAM_HEIGHT, fullSpanZ],
    });

    // Center Vertical Top Beam: from outer top to inner frame top
    if (innerMinZ > outerMinZ + 1.0) {
      const spanZ = innerMinZ - outerMinZ;
      beams.push({
        key: 'grid-beam-v-mid-top',
        position: [xMid, gantryBeamY, (outerMinZ + innerMinZ) / 2],
        size: [BEAM_THICKNESS, BEAM_HEIGHT, spanZ],
      });
    }

    // Center Vertical Bottom Beam: from inner frame bottom to outer bottom
    if (outerMaxZ > innerMaxZ + 1.0) {
      const spanZ = outerMaxZ - innerMaxZ;
      beams.push({
        key: 'grid-beam-v-mid-bottom',
        position: [xMid, gantryBeamY, (innerMaxZ + outerMaxZ) / 2],
        size: [BEAM_THICKNESS, BEAM_HEIGHT, spanZ],
      });
    }

    // Right Vertical Beam: runs fully from outer top to outer bottom
    beams.push({
      key: 'grid-beam-v-right',
      position: [xRight, gantryBeamY, (outerMinZ + outerMaxZ) / 2],
      size: [BEAM_THICKNESS, BEAM_HEIGHT, fullSpanZ],
    });

    return beams;
  }, [outerGrid]);

  return (
    <group>
      {/* Outer Boundary Frame Tie Beams Connecting Inner Grid to Outer Perimeter */}
      {outerConnectingBeams.map((beam) => (
        <mesh
          key={beam.key}
          position={beam.position}
          castShadow
          receiveShadow
        >
          <boxGeometry args={beam.size} />
          <meshStandardMaterial
            color={GANTRY_WHITE}
            metalness={GANTRY_MAT.metalness}
            roughness={GANTRY_MAT.roughness}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>
      ))}

      {/* Horizontal Clusters Gantries */}
      {horizontalClusters.map((cluster, cIdx) => {
        const groupY = getElement3DGroupY(cluster[0].element, undefined, railElevation);
        const { extMinX, extMaxX, minZ, maxZ, archCoords } =
          getHorizontalClusterArchCoords(cluster);

        const extL = extMaxX - extMinX;
        const transSpan = maxZ - minZ + 80.0;
        const transCenterZ = (minZ + maxZ) / 2;

        return (
          <group key={`h-cluster-${cIdx}`}>
            {/* 1. Gantry Arches Transverse Beams */}
            {archCoords.map((gx, gIdx) => (
              <mesh key={`arch-beam-${gIdx}`} position={[gx, groupY + gantryTopY, transCenterZ]}>
                <boxGeometry args={[2.5, 1.5, transSpan]} />
                <meshStandardMaterial
                  color={GANTRY_WHITE}
                  metalness={GANTRY_MAT.metalness}
                  roughness={GANTRY_MAT.roughness}
                  transparent={opacity < 1}
                  opacity={opacity}
                />
              </mesh>
            ))}

            {/* Cluster-Wide Left Outer Longitudinal Beam */}
            <mesh position={[(extMinX + extMaxX) / 2, groupY + gantryTopY, minZ - 40.0]}>
              <boxGeometry args={[extL, 1.5, 1.5]} />
              <meshStandardMaterial
                color={GANTRY_WHITE}
                metalness={GANTRY_MAT.metalness}
                roughness={GANTRY_MAT.roughness}
                transparent={opacity < 1}
                opacity={opacity}
              />
            </mesh>
            {/* Cluster-Wide Right Outer Longitudinal Beam */}
            <mesh position={[(extMinX + extMaxX) / 2, groupY + gantryTopY, maxZ + 40.0]}>
              <boxGeometry args={[extL, 1.5, 1.5]} />
              <meshStandardMaterial
                color={GANTRY_WHITE}
                metalness={GANTRY_MAT.metalness}
                roughness={GANTRY_MAT.roughness}
                transparent={opacity < 1}
                opacity={opacity}
              />
            </mesh>

            {/* 2. Middle beams and side-mounted suspension hangers for each rail in the cluster */}
            {cluster.map((r) => {
              const rz = r.centerWorldZ;
              let rMinX = r.minWorldX;
              let rMaxX = r.maxWorldX;

              // Extend rMinX / rMaxX to meet any crossing or adjacent vertical rail centerlines
              verticalRails.forEach((v) => {
                if (v.minWorldZ <= rz + 60.0 && v.maxWorldZ >= rz - 60.0) {
                  if (Math.abs(v.centerWorldX - r.minWorldX) < 60.0) {
                    rMinX = Math.min(rMinX, v.centerWorldX);
                  }
                  if (Math.abs(v.centerWorldX - r.maxWorldX) < 60.0) {
                    rMaxX = Math.max(rMaxX, v.centerWorldX);
                  }
                }
              });

              // Also clamp to gantry frame extents
              rMinX = Math.max(extMinX, rMinX);
              rMaxX = Math.min(extMaxX, rMaxX);

              const rWidth = Math.max(1, rMaxX - rMinX);
              const rxCenter = (rMinX + rMaxX) / 2;

              const rodMinX = Math.max(rMinX, r.minWorldX);
              const rodMaxX = Math.min(rMaxX, r.maxWorldX);
              const connectingRodXs = calculateInsetRodPositions(rodMinX, rodMaxX, 85.0, 14.0).filter(
                (rx) => !junctionCenters.some((j) => Math.hypot(rx - j.x, rz - j.z) < 45.0)
              );
              const sideOffsetZ = 2.4;

              return (
                <group key={r.element.id}>
                  {/* Middle Support Beam */}
                  <mesh position={[rxCenter, groupY + gantryTopY, rz]}>
                    <boxGeometry args={[rWidth, 1.2, 1.4]} />
                    <meshStandardMaterial
                      color={GANTRY_WHITE}
                      metalness={GANTRY_MAT.metalness}
                      roughness={GANTRY_MAT.roughness}
                      transparent={opacity < 1}
                      opacity={opacity}
                    />
                  </mesh>

                  {/* Side-Mounted Suspension Hangers with Bottom Underside Saddle Supports */}
                  {connectingRodXs.map((rx, rodIdx) => (
                    <group key={`rod-${rodIdx}`}>
                      {/* Top Horizontal Extension Arm (overhead beam to side drop rod) */}
                      <mesh position={[rx, groupY + gantryTopY, rz + sideOffsetZ * 0.5]}>
                        <boxGeometry args={[0.8, 0.45, sideOffsetZ + 0.4]} />
                        <meshStandardMaterial
                          color={GANTRY_WHITE}
                          metalness={GANTRY_MAT.metalness}
                          roughness={GANTRY_MAT.roughness}
                          transparent={opacity < 1}
                          opacity={opacity}
                        />
                      </mesh>

                      {/* Top Rod Collar Fitting */}
                      <mesh position={[rx, groupY + gantryTopY - 0.4, rz + sideOffsetZ]}>
                        <boxGeometry args={[0.7, 0.4, 0.7]} />
                        <meshStandardMaterial
                          color={GANTRY_WHITE}
                          metalness={GANTRY_MAT.metalness}
                          roughness={GANTRY_MAT.roughness}
                          transparent={opacity < 1}
                          opacity={opacity}
                        />
                      </mesh>

                      {/* Side Vertical Suspender Rod (offset safely outside OHT path) */}
                      <mesh position={[rx, groupY + (gantryTopY - 0.7) / 2, rz + sideOffsetZ]}>
                        <cylinderGeometry args={[0.2, 0.2, gantryTopY + 0.7, 12]} />
                        <meshStandardMaterial
                          color={GANTRY_WHITE_EDGE}
                          metalness={GANTRY_MAT_SOFT.metalness}
                          roughness={GANTRY_MAT_SOFT.roughness}
                          transparent={opacity < 1}
                          opacity={opacity}
                        />
                      </mesh>

                      {/* Bottom Cantilever Arm (reaching underneath track from side) */}
                      <mesh position={[rx, groupY - 0.6, rz + sideOffsetZ * 0.5]}>
                        <boxGeometry args={[0.8, 0.35, sideOffsetZ + 0.4]} />
                        <meshStandardMaterial
                          color={GANTRY_WHITE_SOFT}
                          metalness={GANTRY_MAT.metalness}
                          roughness={GANTRY_MAT.roughness}
                          transparent={opacity < 1}
                          opacity={opacity}
                        />
                      </mesh>

                      {/* Bottom Underside Track Support Saddle (100% clear of top running surface) */}
                      <group position={[rx, groupY - 0.35, rz]}>
                        {/* Under-Track Base Cradle Plate */}
                        <mesh position={[0, 0, 0]}>
                          <boxGeometry args={[1.8, 0.30, 1.25]} />
                          <meshStandardMaterial
                            color={GANTRY_WHITE_SOFT}
                            metalness={GANTRY_MAT.metalness}
                            roughness={GANTRY_MAT.roughness}
                            transparent={opacity < 1}
                            opacity={opacity}
                          />
                        </mesh>
                        {/* Lower Side Gripping Flanges */}
                        <mesh position={[0, 0.2, -0.58]}>
                          <boxGeometry args={[1.8, 0.40, 0.16]} />
                          <meshStandardMaterial
                            color={GANTRY_WHITE_EDGE}
                            metalness={GANTRY_MAT_SOFT.metalness}
                            roughness={GANTRY_MAT_SOFT.roughness}
                            transparent={opacity < 1}
                            opacity={opacity}
                          />
                        </mesh>
                        <mesh position={[0, 0.2, 0.58]}>
                          <boxGeometry args={[1.8, 0.40, 0.16]} />
                          <meshStandardMaterial
                            color={GANTRY_WHITE_EDGE}
                            metalness={GANTRY_MAT_SOFT.metalness}
                            roughness={GANTRY_MAT_SOFT.roughness}
                            transparent={opacity < 1}
                            opacity={opacity}
                          />
                        </mesh>
                      </group>
                    </group>
                  ))}
                </group>
              );
            })}
          </group>
        );
      })}

      {/* Vertical Clusters Gantries */}
      {verticalClusters.map((cluster, cIdx) => {
        const groupY = getElement3DGroupY(cluster[0].element, undefined, railElevation);
        const { extMinZ, extMaxZ, minX, maxX, archCoords } = getVerticalClusterArchCoords(cluster);

        const extL = extMaxZ - extMinZ;
        const transSpan = maxX - minX + 80.0;
        const transCenterX = (minX + maxX) / 2;

        return (
          <group key={`v-cluster-${cIdx}`}>
            {/* 1. Gantry Arches Transverse Beams */}
            {archCoords.map((gz, gIdx) => (
              <mesh key={`arch-beam-${gIdx}`} position={[transCenterX, groupY + gantryTopY, gz]}>
                <boxGeometry args={[transSpan, 1.2, 2.0]} />
                <meshStandardMaterial
                  color={GANTRY_WHITE}
                  metalness={GANTRY_MAT.metalness}
                  roughness={GANTRY_MAT.roughness}
                  transparent={opacity < 1}
                  opacity={opacity}
                />
              </mesh>
            ))}

            {/* Cluster-Wide Left Outer Longitudinal Beam */}
            <mesh position={[minX - 40.0, groupY + gantryTopY, (extMinZ + extMaxZ) / 2]}>
              <boxGeometry args={[1.5, 1.5, extL]} />
              <meshStandardMaterial
                color={GANTRY_WHITE}
                metalness={GANTRY_MAT.metalness}
                roughness={GANTRY_MAT.roughness}
                transparent={opacity < 1}
                opacity={opacity}
              />
            </mesh>
            {/* Cluster-Wide Right Outer Longitudinal Beam */}
            <mesh position={[maxX + 40.0, groupY + gantryTopY, (extMinZ + extMaxZ) / 2]}>
              <boxGeometry args={[1.5, 1.5, extL]} />
              <meshStandardMaterial
                color={GANTRY_WHITE}
                metalness={GANTRY_MAT.metalness}
                roughness={GANTRY_MAT.roughness}
                transparent={opacity < 1}
                opacity={opacity}
              />
            </mesh>

            {/* 2. Middle beams and suspender rods for each rail in the cluster */}
            {cluster.map((r) => {
              const rx = r.centerWorldX;
              let rMinZ = r.minWorldZ;
              let rMaxZ = r.maxWorldZ;

              // Extend rMinZ / rMaxZ to meet any crossing or adjacent horizontal rail centerlines
              horizontalRails.forEach((h) => {
                if (h.minWorldX <= rx + 60.0 && h.maxWorldX >= rx - 60.0) {
                  if (Math.abs(h.centerWorldZ - r.minWorldZ) < 60.0) {
                    rMinZ = Math.min(rMinZ, h.centerWorldZ);
                  }
                  if (Math.abs(h.centerWorldZ - r.maxWorldZ) < 60.0) {
                    rMaxZ = Math.max(rMaxZ, h.centerWorldZ);
                  }
                }
              });

              // Also clamp to gantry frame extents
              rMinZ = Math.max(extMinZ, rMinZ);
              rMaxZ = Math.min(extMaxZ, rMaxZ);

              const rHeight = Math.max(1, rMaxZ - rMinZ);
              const rzCenter = (rMinZ + rMaxZ) / 2;

              const rodMinZ = Math.max(rMinZ, r.minWorldZ);
              const rodMaxZ = Math.min(rMaxZ, r.maxWorldZ);
              const connectingRodZs = calculateInsetRodPositions(rodMinZ, rodMaxZ, 85.0, 14.0).filter(
                (rz) => !junctionCenters.some((j) => Math.hypot(rx - j.x, rz - j.z) < 45.0)
              );
              const sideOffsetX = 2.4;

              return (
                <group key={r.element.id}>
                  {/* Middle Support Beam */}
                  <mesh position={[rx, groupY + gantryTopY, rzCenter]}>
                    <boxGeometry args={[1.4, 1.2, rHeight]} />
                    <meshStandardMaterial
                      color={GANTRY_WHITE}
                      metalness={GANTRY_MAT.metalness}
                      roughness={GANTRY_MAT.roughness}
                      transparent={opacity < 1}
                      opacity={opacity}
                    />
                  </mesh>

                  {/* Side-Mounted Suspension Hangers with Bottom Underside Saddle Supports */}
                  {connectingRodZs.map((rz, rodIdx) => (
                    <group key={`rod-${rodIdx}`}>
                      {/* Top Horizontal Extension Arm */}
                      <mesh position={[rx + sideOffsetX * 0.5, groupY + gantryTopY, rz]}>
                        <boxGeometry args={[sideOffsetX + 0.4, 0.45, 0.8]} />
                        <meshStandardMaterial
                          color={GANTRY_WHITE}
                          metalness={GANTRY_MAT.metalness}
                          roughness={GANTRY_MAT.roughness}
                          transparent={opacity < 1}
                          opacity={opacity}
                        />
                      </mesh>

                      {/* Top Rod Collar Fitting */}
                      <mesh position={[rx + sideOffsetX, groupY + gantryTopY - 0.4, rz]}>
                        <boxGeometry args={[0.7, 0.4, 0.7]} />
                        <meshStandardMaterial
                          color={GANTRY_WHITE}
                          metalness={GANTRY_MAT.metalness}
                          roughness={GANTRY_MAT.roughness}
                          transparent={opacity < 1}
                          opacity={opacity}
                        />
                      </mesh>

                      {/* Side Vertical Suspender Rod (offset safely outside OHT path) */}
                      <mesh position={[rx + sideOffsetX, groupY + (gantryTopY - 0.7) / 2, rz]}>
                        <cylinderGeometry args={[0.2, 0.2, gantryTopY + 0.7, 12]} />
                        <meshStandardMaterial
                          color={GANTRY_WHITE_EDGE}
                          metalness={GANTRY_MAT_SOFT.metalness}
                          roughness={GANTRY_MAT_SOFT.roughness}
                          transparent={opacity < 1}
                          opacity={opacity}
                        />
                      </mesh>

                      {/* Bottom Cantilever Arm (reaching underneath track from side) */}
                      <mesh position={[rx + sideOffsetX * 0.5, groupY - 0.6, rz]}>
                        <boxGeometry args={[sideOffsetX + 0.4, 0.35, 0.8]} />
                        <meshStandardMaterial
                          color={GANTRY_WHITE_SOFT}
                          metalness={GANTRY_MAT.metalness}
                          roughness={GANTRY_MAT.roughness}
                          transparent={opacity < 1}
                          opacity={opacity}
                        />
                      </mesh>

                      {/* Bottom Underside Track Support Saddle (100% clear of top running surface) */}
                      <group position={[rx, groupY - 0.35, rz]}>
                        {/* Under-Track Base Cradle Plate */}
                        <mesh position={[0, 0, 0]}>
                          <boxGeometry args={[1.25, 0.30, 1.8]} />
                          <meshStandardMaterial
                            color={GANTRY_WHITE_SOFT}
                            metalness={GANTRY_MAT.metalness}
                            roughness={GANTRY_MAT.roughness}
                            transparent={opacity < 1}
                            opacity={opacity}
                          />
                        </mesh>
                        {/* Lower Side Gripping Flanges */}
                        <mesh position={[-0.58, 0.2, 0]}>
                          <boxGeometry args={[0.16, 0.40, 1.8]} />
                          <meshStandardMaterial
                            color={GANTRY_WHITE_EDGE}
                            metalness={GANTRY_MAT_SOFT.metalness}
                            roughness={GANTRY_MAT_SOFT.roughness}
                            transparent={opacity < 1}
                            opacity={opacity}
                          />
                        </mesh>
                        <mesh position={[0.58, 0.2, 0]}>
                          <boxGeometry args={[0.16, 0.40, 1.8]} />
                          <meshStandardMaterial
                            color={GANTRY_WHITE_EDGE}
                            metalness={GANTRY_MAT_SOFT.metalness}
                            roughness={GANTRY_MAT_SOFT.roughness}
                            transparent={opacity < 1}
                            opacity={opacity}
                          />
                        </mesh>
                      </group>
                    </group>
                  ))}
                </group>
              );
            })}
          </group>
        );
      })}

      {/* Top Overhead Support Beam & Side-Mounted Connecting Rod for Curves */}
      {overheadCurveElements.map((el) => {
        const groupY = getElement3DGroupY(el, undefined, railElevation);
        const rotY = -((el.rotation || 0) * Math.PI) / 180;
        const w = el.dimensions.width;
        const h = el.dimensions.height;
        const railType = resolveRailType(el);

        const radius = getCurveRadius(w, h);
        const sweepAngle = getCurveSweepAngle(railType);
        const sweepRad = (sweepAngle * Math.PI) / 180;

        // Mid-point of the curve arc (apex of the corner)
        const midAngle = sweepRad / 2;
        const cosMid = Math.cos(midAngle);
        const sinMid = Math.sin(midAngle);
        const midLx = radius * cosMid;
        const midLz = radius * sinMid;
        const curveSideOffset = 2.4;
        const outLx = (radius + curveSideOffset) * cosMid;
        const outLz = (radius + curveSideOffset) * sinMid;

        return (
          <group
            key={`top-curve-${el.id}`}
            position={[el.position.x, groupY + gantryTopY, el.position.y]}
            rotation={[0, rotY, 0]}
          >
            {/* Top Curved Support Beam */}
            <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[radius, 0.7, 8, 24, sweepRad]} />
              <meshStandardMaterial
                color={GANTRY_WHITE}
                metalness={GANTRY_MAT.metalness}
                roughness={GANTRY_MAT.roughness}
                transparent={opacity < 1}
                opacity={opacity}
              />
            </mesh>

            {/* Top Outrigger Extension Arm to Side Drop Rod */}
            <mesh position={[(midLx + outLx) / 2, 0, (midLz + outLz) / 2]}>
              <boxGeometry args={[0.8, 0.45, curveSideOffset + 0.4]} />
              <meshStandardMaterial
                color={GANTRY_WHITE}
                metalness={GANTRY_MAT.metalness}
                roughness={GANTRY_MAT.roughness}
                transparent={opacity < 1}
                opacity={opacity}
              />
            </mesh>

            {/* Side Drop Rod (radially outside curve, 100% clear of top running surface) */}
            <group position={[outLx, 0, outLz]}>
              <mesh position={[0, -(gantryTopY + 0.7) / 2, 0]}>
                <cylinderGeometry args={[0.2, 0.2, gantryTopY + 0.7, 12]} />
                <meshStandardMaterial
                  color={GANTRY_WHITE_EDGE}
                  metalness={GANTRY_MAT_SOFT.metalness}
                  roughness={GANTRY_MAT_SOFT.roughness}
                  transparent={opacity < 1}
                  opacity={opacity}
                />
              </mesh>
              {/* Bottom Cantilever Arm reaching inwards under track */}
              <mesh position={[-(outLx - midLx) / 2, -gantryTopY - 0.6, -(outLz - midLz) / 2]}>
                <boxGeometry args={[0.8, 0.35, curveSideOffset + 0.4]} />
                <meshStandardMaterial
                  color={GANTRY_WHITE_SOFT}
                  metalness={GANTRY_MAT.metalness}
                  roughness={GANTRY_MAT.roughness}
                  transparent={opacity < 1}
                  opacity={opacity}
                />
              </mesh>
            </group>

            {/* Bottom Underside Track Support Saddle at Curve Apex */}
            <mesh position={[midLx, -gantryTopY - 0.35, midLz]}>
              <boxGeometry args={[1.5, 0.30, 1.5]} />
              <meshStandardMaterial
                color={GANTRY_WHITE_SOFT}
                metalness={GANTRY_MAT.metalness}
                roughness={GANTRY_MAT.roughness}
                transparent={opacity < 1}
                opacity={opacity}
              />
            </mesh>
          </group>
        );
      })}
    </group>
  );
};

const StaticFabCanvasSpaceScene3D = React.memo(function StaticFabCanvasSpaceScene3D({
  boundsCx,
  boundsCz,
  slabSize,
  rawW,
  rawH,
  floorSize,
  hasDxfFloor,
  darkMode,
  setFloorBusy,
  fabFloorSurfaceOpacity,
  elements,
  visibleIds,
  selectedIds,
  conflictIdSet,
  stockerFoupsMap,
  floorPlanScaleFactor,
  dxfUnitsPerMeter,
  hasFloorCal,
  showSupportPillars,
  batchedRailIds,
  batchedJunctionIds,
  handleSelectElement,
  simulationRunning,
  trackNodeAnnotations,
}: {
  boundsCx: number;
  boundsCz: number;
  slabSize: number;
  rawW: number;
  rawH: number;
  floorSize: number;
  hasDxfFloor: boolean;
  darkMode: boolean;
  setFloorBusy: (busy: boolean) => void;
  fabFloorSurfaceOpacity: number;
  elements: Record<string, LayoutElement>;
  visibleIds: string[];
  selectedIds: string[];
  conflictIdSet: ReadonlySet<string>;
  stockerFoupsMap: Record<string, number>;
  floorPlanScaleFactor?: number;
  dxfUnitsPerMeter?: number;
  hasFloorCal: boolean;
  showSupportPillars: boolean;
  batchedRailIds: ReadonlySet<string>;
  batchedJunctionIds: ReadonlySet<string>;
  handleSelectElement: (id: string) => void;
  simulationRunning: boolean;
  trackNodeAnnotations: TrackNodeAnnotation[];
}) {
  const fabFloor = resolveFabFloorSize(rawW, rawH, slabSize);
  return (
    <>
      <CleanroomFloor
        width={fabFloor.width}
        height={fabFloor.height}
        position={[boundsCx, -0.15, boundsCz]}
        color={VIEW3D_FLOOR_COLOR}
        darkMode={darkMode}
        autoDetail
        glassWhite
        surfaceOpacity={fabFloorSurfaceOpacity}
      />

      {hasDxfFloor && (
        <SimulationFloorPlanTexture3D
          darkMode={darkMode}
          boundsSize={floorSize}
          onComputingChange={setFloorBusy}
        />
      )}

      <EquipmentLayer3D />
      <DxfEquipmentInstances3D />
      <LayoutEquipmentLayer3D
        elements={elements}
        elementIds={visibleIds}
        selectedIds={selectedIds}
        conflictIds={conflictIdSet}
        stockerFoups={stockerFoupsMap}
        floorPlanScaleFactor={floorPlanScaleFactor}
        dxfUnitsPerMeter={dxfUnitsPerMeter}
        hasFloorCalibration={hasFloorCal}
      />

      {showSupportPillars && <UnifiedGantrySystem3D elements={elements} opacity={1} />}

      <AmhsKitTemplateCapture />

      <InstancedTrack
        elements={elements}
        elementIds={visibleIds}
        floorPlanScaleFactor={floorPlanScaleFactor}
        dxfUnitsPerMeter={dxfUnitsPerMeter}
        hasFloorCal={hasFloorCal}
        onSelect={handleSelectElement}
      />

      <InstancedJunction
        elements={elements}
        elementIds={visibleIds}
        floorPlanScaleFactor={floorPlanScaleFactor}
        dxfUnitsPerMeter={dxfUnitsPerMeter}
        hasFloorCal={hasFloorCal}
        onSelect={handleSelectElement}
      />

      {visibleIds.map((id) => {
        const el = elements[id];
        if (!el) return null;
        if (isEquipmentLayoutElement(el)) return null;
        if (batchedRailIds.has(id)) return null;
        if (batchedJunctionIds.has(id)) return null;
        if (simulationRunning && el.type === 'oht-vehicle') return null;
        if (
          !showSupportPillars &&
          (el.type === 'support-pillar' ||
            el.type === 'horizontal-beam' ||
            el.type === 'suspension-rod')
        ) {
          return null;
        }
        return (
          <SchematicElement3D
            key={id}
            element={el}
            isConflict={conflictIdSet.has(id)}
            storedFoupsCount={stockerFoupsMap[id] || 0}
            layoutElements={elements}
            floorPlanScaleFactor={floorPlanScaleFactor}
            dxfUnitsPerMeter={dxfUnitsPerMeter}
            hasFloorCalibration={hasFloorCal}
          />
        );
      })}

      <RouteEndpointFlags3D elements={elements} annotations={trackNodeAnnotations} />
    </>
  );
});

const StaticFabScaledScene3D = React.memo(function StaticFabScaledScene3D({
  bounds,
  elementOrder,
  hasDxfFloor,
  darkMode,
  setFloorBusy,
  fabFloorSurfaceOpacity,
  useDetailedMeshes,
  floorIds,
  elements,
  hiddenLayerIds,
  precomputedBatches,
  setInstanceBusy,
  meshIds,
  showSupportPillars,
  conflictIdSet,
  stockerFoupsMap,
  visibleIds,
  trackNodeAnnotations,
}: {
  bounds: ReturnType<typeof computeSceneBounds>;
  elementOrder: string[];
  hasDxfFloor: boolean;
  darkMode: boolean;
  setFloorBusy: (busy: boolean) => void;
  fabFloorSurfaceOpacity: number;
  useDetailedMeshes: boolean;
  floorIds: string[];
  elements: Record<string, LayoutElement>;
  hiddenLayerIds: Set<string>;
  precomputedBatches: any;
  setInstanceBusy: (busy: boolean) => void;
  meshIds: string[];
  showSupportPillars: boolean;
  conflictIdSet: ReadonlySet<string>;
  stockerFoupsMap: Record<string, number>;
  visibleIds: string[];
  trackNodeAnnotations: TrackNodeAnnotation[];
}) {
  const fabFloor = resolveFabFloorSize(bounds.span, bounds.span, bounds.span);
  return (
    <group position={[-bounds.cx, 0, -bounds.cz]}>
      <group scale={[bounds.scale, bounds.scale, bounds.scale]}>
        {elementOrder.length > 0 && (
          <CleanroomFloor
            width={fabFloor.width}
            height={fabFloor.height}
            position={[bounds.cx, -0.15, bounds.cz]}
            color={VIEW3D_FLOOR_COLOR}
            darkMode={darkMode}
            autoDetail
            glassWhite
            surfaceOpacity={fabFloorSurfaceOpacity}
          />
        )}

        {hasDxfFloor && (
          <SimulationFloorPlanTexture3D
            darkMode={darkMode}
            boundsSize={bounds.span}
            onComputingChange={setFloorBusy}
          />
        )}

        {!useDetailedMeshes && floorIds.length > 0 && (
          <InstancedElements3D
            elements={elements}
            elementOrder={floorIds}
            hiddenLayerIds={hiddenLayerIds}
            precomputedBatches={precomputedBatches}
            onComputingChange={setInstanceBusy}
          />
        )}

        {meshIds.map((id) => {
          const el = elements[id];
          if (!el) return null;
          if (
            !showSupportPillars &&
            (el.type === 'support-pillar' ||
              el.type === 'horizontal-beam' ||
              el.type === 'suspension-rod')
          ) {
            return null;
          }
          return (
            <FactoryElement3D
              key={id}
              element={el}
              isConflict={conflictIdSet.has(id)}
              storedFoupsCount={stockerFoupsMap[id] || 0}
            />
          );
        })}

        {useDetailedMeshes &&
          visibleIds.map((id) => {
            const el = elements[id];
            if (!el) return null;
            if (
              !showSupportPillars &&
              (el.type === 'support-pillar' ||
                el.type === 'horizontal-beam' ||
                el.type === 'suspension-rod')
            ) {
              return null;
            }
            return (
              <FactoryElement3D
                key={id}
                element={el}
                isConflict={conflictIdSet.has(id)}
                storedFoupsCount={stockerFoupsMap[id] || 0}
              />
            );
          })}

        <RouteEndpointFlags3D elements={elements} annotations={trackNodeAnnotations} />
      </group>
    </group>
  );
});

const SceneContent = React.memo(function SceneContent({
  bounds,
  fitFullLayout,
  onComputingChange,
  useCanvasSpace3D,
  sceneBounds,
  activeContentBounds,
  onFitComplete,
  simulationFrames,
  stockerFoups = {},
  activeCameraOhtId,
}: {
  bounds: ReturnType<typeof computeSceneBounds>;
  fitFullLayout: boolean;
  onComputingChange: (busy: boolean) => void;
  useCanvasSpace3D: boolean;
  sceneBounds: FloorPlanBounds | null;
  activeContentBounds?: FloorPlanBounds | null;
  onFitComplete: () => void;
  simulationFrames: FrameVehicle[];
  stockerFoups?: Record<string, number>;
  activeCameraOhtId: string | null;
}) {
  const darkMode = useAppSelector((s) => s.ui.darkMode);
  const layoutElements = useAppSelector((s) => s.layout.elements);
  const layoutOrder = useAppSelector((s) => s.layout.elementOrder);
  const layoutLayers = useAppSelector((s) => s.layout.layers);
  const trackNodeAnnotations = useAppSelector((s) => s.layout.trackNodeAnnotations);
  const validation = useAppSelector((s) => s.validation);
  const layout3d = useAppSelector((s) => s.layout3d);
  const simulationRunning = useAppSelector((s) => s.simulation.running);
  const dragLayout = useAppSelector((s) => s.ui.view3dDragLayout);
  const activeCameraPreset = useAppSelector((s) => s.ui.active3DCameraPreset ?? 'default');
  const cameraPresetVersion = useAppSelector((s) => s.ui.cameraPresetVersion ?? 0);
  const selectedIds = useAppSelector((s) => s.selection.selectedIds);

  const useAutoGen =
    layout3d.autoGenerated && layout3d.status === 'ready' && layoutOrder.length === 0;

  const elements = useAutoGen ? layout3d.elements : layoutElements;
  const elementOrder = useAutoGen ? layout3d.elementOrder : layoutOrder;

  // One rail plane for the whole scene, high enough to clear the tallest tool.
  const railElevation = useMemo(() => resolveRailElevation(Object.values(elements)), [elements]);

  const importedBatches =
    !layout3d.autoGenerated && layout3d.instanceBatches?.length ? layout3d.instanceBatches : null;
  const precomputedBatches = useAutoGen ? layout3d.instanceBatches : importedBatches;

  const activeCameraOht =
    activeCameraOhtId && elements[activeCameraOhtId]?.type === 'oht-vehicle'
      ? elements[activeCameraOhtId]
      : null;
  const activeCameraFrame = activeCameraOhtId
    ? (simulationFrames.find((f) => f.id === activeCameraOhtId) ?? null)
    : null;

  const stockerFoupsMap = stockerFoups;

  /** Small layouts (e.g. test JSON) use detailed meshes for every element. */
  const useDetailedMeshes =
    elementOrder.length > 0 && elementOrder.length <= SMALL_DETAILED_MESH_LIMIT;

  const hiddenLayerIds = useMemo(() => {
    const hidden = new Set<string>();
    for (const [id, layer] of Object.entries(layoutLayers)) {
      if (!layer.visible) hidden.add(id);
    }
    return hidden;
  }, [layoutLayers]);

  const visibleIds = useMemo(() => {
    return elementOrder.filter((id) => {
      const el = elements[id];
      if (!el || !isElementVisible(el)) return false;
      if (isLayerHidden(el, hiddenLayerIds)) return false;
      return true;
    });
  }, [elementOrder, elements, hiddenLayerIds]);

  const floorIds = useMemo(() => {
    if (useDetailedMeshes) return [];
    return visibleIds.filter((id) => {
      const el = elements[id];
      if (!el || !isFloor3DElement(el)) return false;
      // STB / OHB / Lifter use plain boxes via FactoryElement3D.
      if (EQUIPMENT_BOX_TYPES.has(el.type)) return false;
      return true;
    });
  }, [visibleIds, elements, useDetailedMeshes]);

  const meshIds = useMemo(() => {
    if (useDetailedMeshes) return [];
    return visibleIds.filter((id) => {
      const el = elements[id];
      if (!el) return false;
      if (EQUIPMENT_BOX_TYPES.has(el.type)) return true;
      return !isFloor3DElement(el);
    });
  }, [visibleIds, elements, useDetailedMeshes]);

  const floorplanVisible = useAppSelector((s) => s.floorplan.visible);
  const floorPolylines = useAppSelector((s) => s.floorplan.polylines);
  const floorBounds = useAppSelector((s) => s.floorplan.bounds);
  const floorPlanScaleFactor = useAppSelector((s) => s.floorplan.scaleFactor);
  const dxfUnitsPerMeter = useAppSelector((s) => s.floorplan.dxfUnitsPerMeter);
  const showSupportPillars = useAppSelector((s) => s.ui.showSupportPillars ?? false);
  const hasFloorCal = Boolean(floorBounds) && floorPolylines.length > 0;

  const conflictIdSet = useMemo(() => {
    const set = new Set<string>();
    for (const c of validation.conflicts) {
      set.add(c.aId);
      set.add(c.bId);
    }
    return set;
  }, [validation.conflicts]);

  const dispatch = useAppDispatch();
  const handleSelectElement = useCallback(
    (id: string) => {
      dispatch(selectElement(id));
    },
    [dispatch]
  );

  const batchedRailIds = useMemo(() => {
    const set = new Set<string>();
    for (const id of visibleIds) {
      const el = elements[id];
      if (!el || !ALL_RAIL_TYPES.has(el.type)) continue;
      const railType = resolveRailType(el);
      if (!isConnectorRailType(railType) && !isJunctionRailType(railType)) {
        set.add(id);
      }
    }
    return set;
  }, [visibleIds, elements]);

  const batchedJunctionIds = useMemo(() => {
    const set = new Set<string>();
    for (const id of visibleIds) {
      const el = elements[id];
      if (!el || !ALL_RAIL_TYPES.has(el.type)) continue;
      if (resolveRailType(el) === 'junction-circle') set.add(id);
    }
    return set;
  }, [visibleIds, elements]);

  const elementBounds = useMemo(
    () => computeLayoutBoundsFromElements(elements, elementOrder),
    [elements, elementOrder]
  );

  const hasDxfFloor = hasVisibleDxfFloorGeometry({
    visible: floorplanVisible,
    polylines: floorPolylines,
    bounds: floorBounds,
  });
  const fabFloorSurfaceOpacity = hasDxfFloor ? FAB_CLEANROOM_FLOOR_OVER_DXF_OPACITY : 1;

  const [floorBusy, setFloorBusy] = useState(false);
  const [instanceBusy, setInstanceBusy] = useState(false);

  const contentBounds = activeContentBounds ?? sceneBounds;
  const canvasLights = useMemo(() => getCanvas3DLightBudget(), []);

  useEffect(() => {
    onComputingChange(floorBusy || instanceBusy);
  }, [floorBusy, instanceBusy, onComputingChange]);

  if (useCanvasSpace3D) {
    const rawBounds = elementBounds ?? layout3d.importBounds ?? floorBounds ?? contentBounds;
    const boundsCx = rawBounds ? (rawBounds.minX + rawBounds.maxX) / 2 : 0;
    const boundsCz = rawBounds ? (rawBounds.minY + rawBounds.maxY) / 2 : 0;
    const rawW = rawBounds ? Math.max(rawBounds.maxX - rawBounds.minX, 0) : 0;
    const rawH = rawBounds ? Math.max(rawBounds.maxY - rawBounds.minY, 0) : 0;

    // World floor is independent of the layout rectangle so the tiled slab
    // stays visible around DXF / regular content instead of clipping to it.
    const maxRawDim = Math.max(rawW, rawH);
    const floorSize = Math.max(maxRawDim * 4, 4000, SCENE_TARGET_SIZE);
    const slabSize = floorSize;

    return (
      <RailElevationContext.Provider value={railElevation}>
        <SceneBackground />
        {/* Bright studio rig: ~70% cool neutral, ~30% warm champagne accent. */}
        <SceneEnvironment3D intensity={canvasLights.environmentIntensity} />
        <FabLighting center={[boundsCx, 0, boundsCz]} span={slabSize} />

        <StaticFabCanvasSpaceScene3D
          boundsCx={boundsCx}
          boundsCz={boundsCz}
          slabSize={slabSize}
          rawW={rawW}
          rawH={rawH}
          floorSize={floorSize}
          hasDxfFloor={hasDxfFloor}
          darkMode={darkMode}
          setFloorBusy={setFloorBusy}
          fabFloorSurfaceOpacity={fabFloorSurfaceOpacity}
          elements={elements}
          visibleIds={visibleIds}
          selectedIds={selectedIds}
          conflictIdSet={conflictIdSet}
          stockerFoupsMap={stockerFoupsMap}
          floorPlanScaleFactor={floorPlanScaleFactor}
          dxfUnitsPerMeter={dxfUnitsPerMeter}
          hasFloorCal={hasFloorCal}
          showSupportPillars={showSupportPillars}
          batchedRailIds={batchedRailIds}
          batchedJunctionIds={batchedJunctionIds}
          handleSelectElement={handleSelectElement}
          simulationRunning={simulationRunning}
          trackNodeAnnotations={trackNodeAnnotations}
        />

        {simulationRunning && <SimulationOverlay3D frames={simulationFrames} />}

        {activeCameraOht ? (
          <OHTCameraRig
            element={activeCameraOht}
            frame={activeCameraFrame}
            useCanvasSpace3D={useCanvasSpace3D}
            bounds={bounds}
          />
        ) : (
          <AnimatedCameraRig
            fitFullLayout={fitFullLayout}
            layoutBounds={contentBounds}
            dragLayout={dragLayout}
            onFitComplete={onFitComplete}
            useCanvasSpace3D={useCanvasSpace3D}
            bounds={bounds}
            preset={activeCameraPreset}
            cameraPresetVersion={cameraPresetVersion}
            activeCameraOhtId={activeCameraOhtId}
            elements={elements}
            selectedIds={selectedIds}
            simulationFrames={simulationFrames}
          />
        )}
      </RailElevationContext.Provider>
    );
  }

  return (
    <RailElevationContext.Provider value={railElevation}>
      <SceneBackground />
      {/* Bright studio rig: ~70% cool neutral, ~30% warm champagne accent. */}
      <SceneEnvironment3D intensity={canvasLights.environmentIntensity} />
      <FabLighting span={Math.max(bounds.span * 1.08, SCENE_TARGET_SIZE)} />

      <StaticFabScaledScene3D
        bounds={bounds}
        elementOrder={elementOrder}
        hasDxfFloor={hasDxfFloor}
        darkMode={darkMode}
        setFloorBusy={setFloorBusy}
        fabFloorSurfaceOpacity={fabFloorSurfaceOpacity}
        useDetailedMeshes={useDetailedMeshes}
        floorIds={floorIds}
        elements={elements}
        hiddenLayerIds={hiddenLayerIds}
        precomputedBatches={precomputedBatches}
        setInstanceBusy={setInstanceBusy}
        meshIds={meshIds}
        showSupportPillars={showSupportPillars}
        conflictIdSet={conflictIdSet}
        stockerFoupsMap={stockerFoupsMap}
        visibleIds={visibleIds}
        trackNodeAnnotations={trackNodeAnnotations}
      />

      {/* 3D uses FAB epoxy floor markings — skip 2D-style grid overlay on the slab. */}

      {activeCameraOht ? (
        <OHTCameraRig
          element={activeCameraOht}
          frame={activeCameraFrame}
          useCanvasSpace3D={useCanvasSpace3D}
          bounds={bounds}
        />
      ) : (
        <AnimatedCameraRig
          fitFullLayout={fitFullLayout}
          layoutBounds={contentBounds}
          dragLayout={dragLayout}
          onFitComplete={onFitComplete}
          useCanvasSpace3D={useCanvasSpace3D}
          bounds={bounds}
          preset={activeCameraPreset}
          activeCameraOhtId={activeCameraOhtId}
          elements={elements}
          selectedIds={selectedIds}
          simulationFrames={simulationFrames}
        />
      )}
    </RailElevationContext.Provider>
  );
});

export type Canvas3DProps = {
  /** When false, parent renders {@link Canvas3DLoadingOverlay} (e.g. EditorLayout). */
  renderLoadingOverlay?: boolean;
  onLoadingStatusChange?: (stage: Scene3DLoadingStage, progress: number | null) => void;
};

const Canvas3D: React.FC<Canvas3DProps> = ({
  renderLoadingOverlay = true,
  onLoadingStatusChange,
}) => {
  const dispatch = useAppDispatch();
  const floorBounds = useAppSelector((s) => s.floorplan.bounds);
  const layoutElements = useAppSelector((s) => s.layout.elements);
  const layoutOrder = useAppSelector((s) => s.layout.elementOrder);
  const layout3d = useAppSelector((s) => s.layout3d);
  const floorplanVisible = useAppSelector((s) => s.floorplan.visible);
  const floorPolylines = useAppSelector((s) => s.floorplan.polylines);
  const view3dOpen = useAppSelector((s) => s.ui.view3dOpen);
  const view3dSimulation = useAppSelector((s) => s.ui.view3dSimulation);
  const view3dDragLayout = useAppSelector((s) => s.ui.view3dDragLayout);
  const showSupportPillars = useAppSelector((s) => s.ui.showSupportPillars ?? false);
  const simulationRunning = useAppSelector((s) => s.simulation.running);
  const activeCameraPreset = useAppSelector((s) => s.ui.active3DCameraPreset ?? 'default');
  const activeCameraOhtId = useAppSelector((s) => s.ui.active3DCameraOhtId);
  const selectedIds = useAppSelector((s) => s.selection.selectedIds);

  // Single shared simulation loop for both the animated vehicle meshes and the
  // OHT onboard camera rig — calling useSimulationFrames more than once would
  // run duplicate RAF loops that double-dispatch pause/stop actions per frame.
  const { vehicles: simulationFrames, stockerFoups } = useSimulationFrames();

  const hasDxfFloor = hasVisibleDxfFloorGeometry({
    visible: floorplanVisible,
    polylines: floorPolylines,
    bounds: floorBounds,
  });
  /** Real content only — empty shell floor plans (name/bounds, no polylines) do not count. */
  const hasReal3dContent = hasDxfFloor || layoutOrder.length > 0;

  /**
   * Demo auto-gen is disabled. Empty editors used to spin "Building 3D layout…"
   * from leftover floor-plan shells; 3D now requires a real layout or DXF.
   */
  const bootstrap = use3dLayoutBootstrap(false);

  const useAutoGen =
    layout3d.autoGenerated && layout3d.status === 'ready' && layoutOrder.length === 0;
  const elements = useAutoGen ? layout3d.elements : layoutElements;
  const elementOrder = useAutoGen ? layout3d.elementOrder : layoutOrder;

  const ohtElements = useMemo(
    () =>
      sortOhtElements(
        elementOrder
          .map((id) => elements[id])
          .filter((el): el is LayoutElement => el?.type === 'oht-vehicle')
      ),
    [elements, elementOrder]
  );

  const selectedElement = useMemo(() => {
    if (!selectedIds || selectedIds.length === 0) return null;
    return elements[selectedIds[0]] ?? null;
  }, [elements, selectedIds]);
  const selectedEquipmentName = useAppSelector((s) => {
    const id = s.equipment.selectedInstanceId;
    return id ? (s.equipment.instances[id]?.name ?? null) : null;
  });
  const hasFocusTarget = Boolean(selectedElement || selectedEquipmentName);

  const storedFollowOhtId = useAppSelector((s) => s.ui.active3DFollowOhtId);
  const followOhtMode = useAppSelector((s) => s.ui.active3DFollowOhtMode);
  const followOhtId = useMemo(
    () =>
      resolveFollowOhtId(
        ohtElements.map((el) => el.id),
        selectedIds[0],
        (id) => elements[id]?.type === 'oht-vehicle',
        storedFollowOhtId
      ),
    [ohtElements, selectedIds, elements, storedFollowOhtId]
  );

  const activeOhtLabel = useMemo(() => {
    const id = activeCameraOhtId || followOhtId;
    if (id && elements[id]?.metadata?.name) return elements[id].metadata.name;
    return ohtElements[0]?.metadata?.name || 'OHT 1';
  }, [activeCameraOhtId, followOhtId, elements, ohtElements]);

  /** Live-sync 3D with everything on the 2D canvas (including newly added shapes). */
  const useLiveLayout3D = !useAutoGen && elementOrder.length > 0;

  /** Match 2D canvas coordinates — used for DXF floor plans and user layouts. */
  const useCanvasSpace3D = hasDxfFloor || useLiveLayout3D;

  // Full native pixel density for a crisp fab view (cap at 2× for perf).
  const canvasDprMax = getCanvas3DDprCap(simulationRunning, elementOrder.length);
  const canvasDpr = useMemo<[number, number]>(
    () => [
      1,
      Math.min(2, canvasDprMax, typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2),
    ],
    [canvasDprMax]
  );
  const canvasGl = useMemo(() => getCanvas3DGlSettings(), []);
  const canvasNear = canvasGl.logarithmicDepthBuffer ? 0.05 : 1;

  const liveLayoutBounds = useMemo(
    () => computeLayoutBoundsFromElements(elements, elementOrder),
    [elements, elementOrder]
  );

  const equipmentInstances = useAppSelector((s) => s.equipment.instances);
  const diagnosticEquipmentCounts = useMemo<Canvas3DEquipmentCounts>(
    () => ({
      layout: elementOrder.reduce(
        (count, id) => count + (LAYOUT_EQUIPMENT_TYPES.has(elements[id]?.type) ? 1 : 0),
        0
      ),
      instances: Object.keys(equipmentInstances).length,
      ohts: ohtElements.length,
    }),
    [elementOrder, elements, equipmentInstances, ohtElements.length]
  );
  /**
   * Dense DXF core for 3D Fit/grid — trims sparse outliers so the fab fills the
   * view (same camera + grid bounds; corner-projection fit handles framing).
   */
  const denseFloorBounds = useMemo(() => {
    if (!floorBounds || floorPolylines.length === 0) return floorBounds;
    return (
      getDenseFitBounds(floorBounds, computePolylineBBoxes(floorPolylines), 0.08) ?? floorBounds
    );
  }, [floorBounds, floorPolylines]);

  const sceneBounds = useMemo(
    () => mergeFloorPlanBounds(denseFloorBounds, liveLayoutBounds ?? layout3d.importBounds),
    [denseFloorBounds, liveLayoutBounds, layout3d.importBounds]
  );

  /**
   * Frame the fab content that should fill the 3D view:
   * - DXF floor → dense floor core (camera + grid share this)
   * - layout-only → elements/equipment focus bounds
   */
  const cameraFocusBounds = useMemo(() => {
    if (hasDxfFloor) {
      const core = denseFloorBounds;
      if (!core) return sceneBounds;
      if (!liveLayoutBounds) return padBoundsProportional(core, 0.02);
      const layoutCx = (liveLayoutBounds.minX + liveLayoutBounds.maxX) / 2;
      const layoutCy = (liveLayoutBounds.minY + liveLayoutBounds.maxY) / 2;
      const corePad = padBoundsProportional(core, 0.12);
      const layoutInsideCore =
        layoutCx >= corePad.minX &&
        layoutCx <= corePad.maxX &&
        layoutCy >= corePad.minY &&
        layoutCy <= corePad.maxY;
      const fabBounds = layoutInsideCore
        ? (mergeFloorPlanBounds(core, liveLayoutBounds) ?? core)
        : core;
      return padBoundsProportional(fabBounds, 0.02);
    }
    return (
      computeCameraFocusBounds(liveLayoutBounds ?? layout3d.importBounds, equipmentInstances) ??
      sceneBounds
    );
  }, [
    hasDxfFloor,
    denseFloorBounds,
    liveLayoutBounds,
    layout3d.importBounds,
    equipmentInstances,
    sceneBounds,
  ]);

  const sceneFloorBounds = denseFloorBounds ?? liveLayoutBounds ?? layout3d.importBounds;

  const bounds = useMemo(
    () => computeSceneBounds(sceneFloorBounds, elements, elementOrder),
    [sceneFloorBounds, elements, elementOrder]
  );

  const [fitFullLayout, setFitFullLayout] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setViewport] = useViewport();
  const [computing, setComputing] = useState(false);
  const [floor3dReady, setFloor3dReady] = useState(false);

  /** DXF 3D uses the real floor plan — cancel stale auto-gen bootstrap loading. */
  useEffect(() => {
    if (!view3dOpen || !hasDxfFloor) return;
    if (layout3d.autoGenerated || (layout3d.status === 'loading' && !layout3d.importBounds)) {
      dispatch(clearLayout3d());
    }
  }, [
    view3dOpen,
    hasDxfFloor,
    layout3d.autoGenerated,
    layout3d.status,
    layout3d.importBounds,
    dispatch,
  ]);

  useEffect(() => {
    if (!view3dOpen) {
      setFloor3dReady(false);
    }
  }, [view3dOpen]);

  const importing3d =
    layout3d.status === 'loading' &&
    Boolean(layout3d.importBounds) &&
    !layout3d.autoGenerated &&
    !useLiveLayout3D;

  const hasContent = hasReal3dContent || bootstrap.ready || Boolean(layout3d.importBounds);

  useEffect(() => {
    if (view3dOpen) {
      setFitFullLayout(false);
    }
  }, [view3dOpen]);

  /** Kick back to 2D if 3D was opened with no layout / fab geometry. */
  useEffect(() => {
    if (!view3dOpen) return;
    if (hasReal3dContent || bootstrap.loading || importing3d) return;
    dispatch(closeView3d());
  }, [view3dOpen, hasReal3dContent, bootstrap.loading, importing3d, dispatch]);

  /** Keep viewport canvas size in sync so 3D framing matches the 2D canvas. */
  useEffect(() => {
    if (!view3dOpen || !containerRef.current) return;
    const el = containerRef.current;
    const applySize = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) {
        setViewport({ canvasWidth: w, canvasHeight: h });
      }
    };
    applySize();
    const ro = new ResizeObserver(applySize);
    ro.observe(el);
    return () => ro.disconnect();
  }, [view3dOpen, setViewport]);

  useEffect(() => {
    if (!view3dOpen || !sceneBounds) return;
    if (hasDxfFloor && computing) return;
    if (hasDxfFloor && !computing) {
      setFloor3dReady(true);
    }
  }, [
    view3dOpen,
    sceneBounds,
    hasDxfFloor,
    computing,
    sceneBounds?.minX,
    sceneBounds?.maxX,
    sceneBounds?.minY,
    sceneBounds?.maxY,
  ]);

  /** Never strand the view behind the loader if floor prep stalls. */
  useEffect(() => {
    if (!view3dOpen || !hasDxfFloor || floor3dReady) return;
    const id = window.setTimeout(() => setFloor3dReady(true), 12000);
    return () => window.clearTimeout(id);
  }, [view3dOpen, hasDxfFloor, floor3dReady]);

  const onFitView = useCallback(() => {
    setFitFullLayout(true);
  }, []);

  const onFitComplete = useCallback(() => {
    setFitFullLayout(false);
  }, []);

  const showBlockingLoader =
    bootstrap.loading || importing3d || (hasDxfFloor && computing && !floor3dReady);

  /**
   * Layout compute finishing is not the same as the scene being on screen: the
   * model assets are still streaming and the first frame still has to compile.
   * Readiness therefore needs the asset loaders quiet and a presented frame.
   */
  const { active: assetsLoading, progress: assetProgress } = useProgress();
  const [sceneRevealed, setSceneRevealed] = useState(false);
  const [gpuReady, setGpuReady] = useState(false);
  const [loaderMinDone, setLoaderMinDone] = useState(false);

  const onSceneGpuReady = useCallback(() => setGpuReady(true), []);

  /** Each 2D → 3D switch shows the loader until GPU frames are warm. */
  useEffect(() => {
    if (!view3dOpen) {
      setSceneRevealed(false);
      setGpuReady(false);
      setLoaderMinDone(false);
      return;
    }
    setSceneRevealed(false);
    setGpuReady(false);
    setLoaderMinDone(false);
    const minTimer = window.setTimeout(() => setLoaderMinDone(true), 900);
    return () => window.clearTimeout(minTimer);
  }, [view3dOpen]);

  // Closing the view (or emptying it) arms the loader again for the next open.
  useEffect(() => {
    if (view3dOpen && hasContent) return;
    setSceneRevealed(false);
    setGpuReady(false);
  }, [view3dOpen, hasContent]);

  /**
   * Dismiss once bootstrap/floor work finishes AND the canvas has presented frames.
   */
  useEffect(() => {
    if (sceneRevealed) return;
    if (!showBlockingLoader && gpuReady && loaderMinDone) {
      setSceneRevealed(true);
    }
  }, [sceneRevealed, showBlockingLoader, gpuReady, loaderMinDone]);

  /** Never strand the user behind the loader if readiness signals stall. */
  useEffect(() => {
    if (sceneRevealed || !view3dOpen) return;
    const id = window.setTimeout(() => setSceneRevealed(true), 12000);
    return () => window.clearTimeout(id);
  }, [sceneRevealed, view3dOpen]);

  const loadingStage: Scene3DLoadingStage = bootstrap.loading
    ? 'layout'
    : importing3d
      ? 'import'
      : hasDxfFloor && computing && !floor3dReady
        ? 'floorplan'
        : assetsLoading
          ? 'assets'
          : !gpuReady
            ? 'opening'
            : 'compile';

  // Only the asset stage knows how much work is left; the rest are indeterminate.
  const loadingProgress = loadingStage === 'assets' ? assetProgress / 100 : null;

  const showSceneLoader = hasContent && !sceneRevealed;

  useEffect(() => {
    dispatch(setView3dSceneRevealed(sceneRevealed));
  }, [dispatch, sceneRevealed]);

  useEffect(() => {
    if (!onLoadingStatusChange) return;
    if (!showSceneLoader) return;
    onLoadingStatusChange(loadingStage, loadingProgress);
  }, [onLoadingStatusChange, showSceneLoader, loadingStage, loadingProgress]);

  useEffect(() => {
    return () => {
      dispatch(setView3dSceneRevealed(false));
    };
  }, [dispatch]);

  const onClose = useCallback(() => {
    if (simulationRunning && view3dSimulation) {
      dispatch(stopSimulation());
    }
    dispatch(toggleView3d());
  }, [dispatch, simulationRunning, view3dSimulation]);

  const onToggleDragLayout = useCallback(() => {
    dispatch(toggleView3dDragLayout());
  }, [dispatch]);

  const onSelectCameraMode = useCallback(
    (val: string) => {
      if (val.startsWith('preset:')) {
        const preset = val.replace('preset:', '') as CameraViewPreset;
        dispatch(setActive3DCameraPreset(preset));
      } else if (val.startsWith('follow-mode:')) {
        dispatch(
          setActive3DFollowOhtMode(val.replace('follow-mode:', '') as FollowOhtCameraMode)
        );
      } else if (val.startsWith('follow:')) {
        dispatch(setActive3DFollowOht(val.replace('follow:', '')));
      } else if (val.startsWith('oht:')) {
        dispatch(setActive3DCameraOht(val.replace('oht:', '')));
      } else {
        dispatch(setActive3DCameraPreset('default'));
      }
    },
    [dispatch]
  );

  const cameraMenuValue = activeCameraOhtId
    ? `oht:${activeCameraOhtId}`
    : activeCameraPreset === 'follow' && followOhtId
      ? `follow:${followOhtId}`
      : `preset:${activeCameraPreset}`;

  const cameraMenuLabel = activeCameraOhtId
    ? `🎥 ${activeOhtLabel} Onboard`
    : activeCameraPreset === 'follow'
      ? `🚘 Follow ${activeOhtLabel}${followOhtMode === 'side' ? ' (Side)' : followOhtMode === 'elevated' ? ' (Elevated)' : ''
      }`
      : activeCameraPreset === 'focus'
        ? `🎯 Focus ${selectedElement?.metadata?.name || selectedElement?.type || selectedEquipmentName || 'equipment'}`
        : activeCameraPreset === 'reference'
          ? '🎯 Reference View'
          : activeCameraPreset === 'front45'
            ? '📐 Front 45°'
            : activeCameraPreset === 'wide'
              ? '🏭 Wide FAB'
              : activeCameraPreset === 'top'
                ? '⬆️ Top View'
                : activeCameraPreset === 'front'
                  ? '👁️ Front View'
                  : activeCameraPreset === 'back'
                    ? '🔙 Back View'
                    : activeCameraPreset === 'left'
                      ? '⬅️ Left Side View'
                      : activeCameraPreset === 'right'
                        ? '➡️ Right Side View'
                        : '📷 Isometric';

  const cameraMenuGroups = useMemo<CameraViewGroup[]>(() => {
    const groups: CameraViewGroup[] = [
      {
        label: 'Reference Camera Views',
        items: [
          { id: 'preset:reference', kind: 'preset', label: '🎯 Reference View (Hero)' },
          { id: 'preset:front45', kind: 'preset', label: '📐 Front 45°' },
          { id: 'preset:wide', kind: 'preset', label: '🏭 Wide FAB' },
        ],
      },
      {
        label: 'Standard Camera Views',
        items: [
          { id: 'preset:default', kind: 'preset', label: '📷 Isometric (Default)' },
          { id: 'preset:top', kind: 'preset', label: '⬆️ Top View' },
          { id: 'preset:front', kind: 'preset', label: '👁️ Front View' },
          { id: 'preset:back', kind: 'preset', label: '🔙 Back View' },
          { id: 'preset:left', kind: 'preset', label: '⬅️ Left Side View' },
          { id: 'preset:right', kind: 'preset', label: '➡️ Right Side View' },
        ],
      },
      {
        label: 'Focus selected equipment',
        items: [
          {
            id: 'preset:focus',
            kind: 'preset',
            label: hasFocusTarget
              ? `🎯 Focus Equipment (${selectedElement?.metadata?.name || selectedElement?.type || selectedEquipmentName})`
              : '🎯 Focus Equipment (No Selection)',
            hint: 'Jump the camera to the selected machine or stocker. Does not follow an OHT.',
            disabled: !hasFocusTarget,
          },
        ],
      },
    ];
    if (ohtElements.length > 0) {
      groups.push({
        label: 'Follow OHT (chase one vehicle)',
        items: ohtElements.map((oht, index) => ({
          id: `follow:${oht.id}`,
          kind: 'follow' as const,
          label: `🚘 Follow ${oht.metadata?.name || `OHT ${index + 1}`}`,
          hint: 'Third-person chase — OHT, track, floor, and nearby equipment stay in frame. Simulation keeps running.',
        })),
      });
      groups.push({
        label: 'Follow OHT camera style',
        items: [
          {
            id: 'follow-mode:chase',
            kind: 'follow' as const,
            label: followOhtMode === 'chase' ? '✓ Chase view (default)' : 'Chase view',
            hint: 'Behind and above the OHT with floor context ahead.',
          },
          {
            id: 'follow-mode:side',
            kind: 'follow' as const,
            label: followOhtMode === 'side' ? '✓ Side view' : 'Side view',
            hint: 'Alongside the vehicle — good for load ports and equipment.',
          },
          {
            id: 'follow-mode:elevated',
            kind: 'follow' as const,
            label: followOhtMode === 'elevated' ? '✓ Elevated view' : 'Elevated view',
            hint: 'Higher and wider — more FAB layout while following.',
          },
        ],
      });
      groups.push({
        label: 'Onboard OHT driver views',
        items: ohtElements.map((oht, index) => ({
          id: `oht:${oht.id}`,
          kind: 'oht' as const,
          label: `🎥 ${oht.metadata?.name || `OHT ${index + 1}`} Onboard`,
          hint: 'Look out from that vehicle’s nose, at the bottom of the track.',
        })),
      });
    }
    return groups;
  }, [followOhtMode, hasFocusTarget, ohtElements, selectedElement, selectedEquipmentName]);

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 bg-white transition-opacity duration-200 ${sceneRevealed ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      aria-hidden={!sceneRevealed}
    >
      <div className="absolute top-3 left-3 z-20 flex items-center gap-2">
        <button
          type="button"
          onClick={onFitView}
          className="rounded-md border border-gray-300 bg-white/95 px-3 py-1.5 text-sm font-semibold text-gray-800 shadow hover:bg-white dark:border-gray-600 dark:bg-gray-900/95 dark:text-gray-100"
          title="Fit entire layout in view"
        >
          ⊡ Fit
        </button>
        <button
          type="button"
          onClick={onToggleDragLayout}
          className={`rounded-md border px-3 py-1.5 text-sm font-semibold shadow ${view3dDragLayout
              ? 'border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-950 dark:text-primary-200'
              : 'border-gray-300 bg-white/95 text-gray-800 hover:bg-white dark:border-gray-600 dark:bg-gray-900/95 dark:text-gray-100'
            }`}
          title="Drag layout — left-click pan (disable orbit)"
        >
          ✋ Drag layout
        </button>
        <button
          type="button"
          onClick={() => dispatch(toggleSupportPillars())}
          className={`rounded-md border px-3 py-1.5 text-sm font-semibold shadow transition-colors ${showSupportPillars
              ? 'border-gray-300 bg-white/95 text-gray-800 hover:bg-white dark:border-gray-600 dark:bg-gray-900/95 dark:text-gray-100'
              : 'border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
            }`}
          title={
            showSupportPillars
              ? 'Hide support pillars and hanging rods'
              : 'Show support pillars and hanging rods'
          }
        >
          {showSupportPillars ? '🏛 Pillars: On' : '🏛 Pillars: Off'}
        </button>
        <CameraViewMenu
          value={cameraMenuValue}
          label={cameraMenuLabel}
          groups={cameraMenuGroups}
          onSelect={onSelectCameraMode}
        />
        {view3dSimulation && (
          <span className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-200">
            3D Simulation
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 z-[70] rounded-md border border-gray-300 bg-white/95 px-3 py-1.5 text-sm font-semibold text-gray-800 shadow hover:bg-white dark:border-gray-600 dark:bg-gray-900/95 dark:text-gray-100"
        title="Exit 3D view"
      >
        ✕ Exit 3D
      </button>

      {renderLoadingOverlay && showSceneLoader && (
        <Canvas3DLoadingOverlay stage={loadingStage} progress={loadingProgress} />
      )}

      {!hasContent && !bootstrap.loading && !importing3d && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-600 dark:text-gray-400">
          Import a JSON layout or DXF floor plan to view in 3D.
        </div>
      )}

      {hasContent && (
        <Canvas
          // DPR up to native screen pixel ratio (cap at 2 for performance)
          dpr={canvasDpr}
          // Demand-render when idle; keep always-on only for live sim / OHT ride-along.
          frameloop={
            simulationRunning ||
              Boolean(activeCameraOhtId) ||
              activeCameraPreset === 'follow' ||
              !sceneRevealed ||
              DEBUG_3D_ENABLED
              ? 'always'
              : 'demand'
          }
          style={{ background: VIEW3D_BACKGROUND }}
          camera={
            useCanvasSpace3D
              ? {
                fov: 40,
                near: canvasNear,
                far: 50000,
                // Front-elevated default (Blender amhs_fab) — Fit applied by SchematicCameraRig.
                position: [
                  1400 * SCHEMATIC_3D_CAMERA_DIR.x,
                  SCHEMATIC_3D_TARGET_Y + 1400 * SCHEMATIC_3D_CAMERA_DIR.y,
                  1400 * SCHEMATIC_3D_CAMERA_DIR.z,
                ],
              }
              : {
                fov: 40,
                near: 0.5,
                far: SCENE_TARGET_SIZE * 25,
                position: [
                  1400 * SCHEMATIC_3D_CAMERA_DIR.x,
                  SCHEMATIC_3D_TARGET_Y + 1400 * SCHEMATIC_3D_CAMERA_DIR.y,
                  1400 * SCHEMATIC_3D_CAMERA_DIR.z,
                ],
              }
          }
          gl={canvasGl}
          onCreated={({ gl, scene, camera }) => {
            // Off-screen kit template capture (layer 1) must not draw or shadow the fab scene.
            camera.layers.disable(1);
            const c = new THREE.Color(VIEW3D_BACKGROUND);
            gl.setClearColor(c, 1);
            gl.outputColorSpace = THREE.SRGBColorSpace;
            const visualConfig = getFabVisualConfig();
            gl.toneMapping = visualConfig.enablePostProcessing
              ? THREE.NoToneMapping
              : getCanvas3DToneMapping();
            gl.toneMappingExposure = getCanvas3DToneMappingExposure();
            configureCanvas3DRenderer(gl);
            scene.background = c;

            if (gl.debug) gl.debug.checkShaderErrors = false;

            const canvasEl = gl.domElement;
            const handleContextLost = (event: Event) => {
              event.preventDefault();
              console.info(
                '[Canvas3D] WebGL context lost. Preventing default to allow automatic restoration...'
              );
            };
            const handleContextRestored = () => {
              console.info('[Canvas3D] WebGL context restored.');
              if (typeof gl.resetState === 'function') gl.resetState();
            };
            canvasEl.addEventListener('webglcontextlost', handleContextLost, false);
            canvasEl.addEventListener('webglcontextrestored', handleContextRestored, false);
          }}
        >
          <SceneContent
            bounds={bounds}
            fitFullLayout={fitFullLayout}
            onComputingChange={setComputing}
            useCanvasSpace3D={useCanvasSpace3D}
            sceneBounds={sceneBounds}
            activeContentBounds={cameraFocusBounds}
            onFitComplete={onFitComplete}
            simulationFrames={simulationFrames}
            stockerFoups={stockerFoups}
            activeCameraOhtId={activeCameraOhtId}
          />
          <CameraInteractionMonitor />
          <Scene3DReadyProbe onReady={onSceneGpuReady} />
          <FabDebugSceneOverride />
          <Canvas3DRenderBudget restingDpr={canvasDpr[1]} />
          <FabPostProcessing />
          {DEBUG_3D_ENABLED && (
            <React.Suspense fallback={null}>
              <DevCanvas3DDiagnostics equipment={diagnosticEquipmentCounts} />
            </React.Suspense>
          )}
        </Canvas>
      )}

      <div className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full border border-gray-300 bg-white/90 px-3.5 py-1 text-xs font-medium text-gray-700 shadow dark:border-gray-600 dark:bg-gray-900/90 dark:text-gray-200">
        {activeCameraOhtId
          ? 'Onboard: looking out from that OHT under the track'
          : activeCameraPreset === 'follow'
            ? `Following ${activeOhtLabel} — third-person chase (OHT, track, floor, equipment). Simulation unchanged.`
            : activeCameraPreset === 'focus'
              ? `Focus: framing ${selectedElement?.metadata?.name || selectedElement?.type || selectedEquipmentName || 'selection'}`
              : activeCameraPreset === 'top'
                ? 'Top view — scroll to zoom toward the floor'
                : view3dDragLayout
                  ? 'Left-drag · pan layout · Scroll · zoom'
                  : 'Left-drag · orbit · Scroll · zoom · Right-drag · pan'}
      </div>

      {layout3d.error && (
        <div className="absolute top-14 left-3 z-20 rounded-md bg-red-50 px-3 py-1.5 text-xs text-red-700 border border-red-200">
          {layout3d.error}
        </div>
      )}
    </div>
  );
};

export default Canvas3D;
